import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, runTransaction, writeBatch, query, orderBy, where, limit, addDoc, increment } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CONFIG, getCloudFunctionUrl } from '../config';
import { db, auth } from '../firebase';
import Spinner from './Spinner';
import ImprimirTicket from './ImprimirTicket';
import InvoicePrinter from './InvoicePrinter';
import Notification from './Notification';
import RemitoPrintInline from './RemitoPrintInline';
import { printShippingLabel } from '../utils/shippingLabelPrint';
import { registerCustomerNewsletterEmails } from '../utils/newsletterCustomerEmail';
import {
  emptyPaymentMethods,
  formatPaymentMethodLabel,
  formatPaymentMethods,
  isConsignmentSale,
  withConsignmentNote
} from '../utils/paymentMethods';
import { addDaysToDate, buildQuoteInvoiceData, DEFAULT_QUOTE_CURRENCY, DEFAULT_QUOTE_DESTINATION, DEFAULT_QUOTE_PAYMENT, DEFAULT_QUOTE_VALID_DAYS, parseLocalDateInput, toDateInputValue } from '../utils/quoteDocument';
import { isViewerFromStorage } from '../utils/staffAccess';
import './LocalSales.css';

// URL de una Cloud Function (directa Cloud Run o BASE_URL + nombre); '' si no hay config
const getAfipFunctionUrl = (name) => {
  try {
    return getCloudFunctionUrl(name);
  } catch {
    return '';
  }
};

// Fetch con timeout real (AbortController); fetch() no soporta la opción timeout
const fetchWithTimeout = (url, options = {}, timeoutMs = 60000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
};

/** Ventas del día en POS: tope de lecturas (un día normal no supera esto). */
const DAILY_SALES_QUERY_LIMIT = 120;

function getTodayLocalBounds() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

// ===========================
// UTILITY FUNCTIONS
// ===========================
const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const toDateValue = (date) => {
  if (!date) return null;
  return typeof date.toDate === 'function' ? date.toDate() : date;
};

const toDateMs = (date) => {
  if (!date) return 0;
  if (typeof date.toMillis === 'function') return date.toMillis();
  if (typeof date.seconds === 'number') return date.seconds * 1000;
  const value = toDateValue(date);
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const ingestFrequentCustomer = (byKey, data, source) => {
  const cd = data?.customerData;
  if (!cd || typeof cd !== 'object') return;
  const name = (cd.name || '').trim();
  const cuitRaw = (cd.cuit || '').trim();
  const cuitDigits = cuitRaw.replace(/\D/g, '');
  if (!name && !cuitDigits) return;
  const key = cuitDigits ? `cuit-${cuitDigits}` : `name-${name.toLowerCase()}`;
  const incoming = {
    name: name || 'Sin nombre',
    cuit: cuitRaw,
    documentType: cd.documentType || '99',
    email: (cd.email || data.invoiceOptions?.emailAddress || data.emailedTo || '').trim(),
    phone: (cd.phone || '').trim(),
    ivaCondition: cd.ivaCondition || '5',
    address: (cd.address || '').trim(),
    city: (cd.city || '').trim(),
    province: (cd.province || '').trim(),
    country: (cd.country || '').trim(),
    postalCode: (cd.postalCode || '').trim(),
    source,
    dateMs: toDateMs(data.date || data.sentAt || data.createdAt)
  };
  const prev = byKey.get(key);
  const newer = !prev || incoming.dateMs >= (prev.dateMs || 0);
  const base = newer ? incoming : prev;
  const extra = newer ? prev : incoming;
  byKey.set(key, {
    ...base,
    email: base.email || extra?.email || '',
    phone: base.phone || extra?.phone || '',
    cuit: base.cuit || extra?.cuit || '',
    address: base.address || extra?.address || '',
    city: base.city || extra?.city || '',
    province: base.province || extra?.province || '',
    country: base.country || extra?.country || '',
    postalCode: base.postalCode || extra?.postalCode || '',
    documentType: base.documentType || extra?.documentType || '99',
    ivaCondition: base.ivaCondition || extra?.ivaCondition || '5'
  });
};

const formatSaleDate = (date) => {
  const value = toDateValue(date);
  if (!value) return '-';
  return value.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const getCustomerNameFromSale = (sale) => sale?.customerData?.name || 'Consumidor Final';

const mapPhysicalSaleToPrintInfo = (sale) => ({
  orderNumber: sale.orderNumber,
  date: toDateValue(sale.date),
  products: sale.products || [],
  customerData: {
    name: getCustomerNameFromSale(sale),
    email: sale.customerData?.email || '',
    cuit: sale.customerData?.cuit || '',
    cuil: sale.customerData?.cuil || sale.customerData?.cuit || '',
    dni: sale.customerData?.dni || '',
    documentType: sale.customerData?.documentType || '99',
    phone: sale.customerData?.phone || '',
    address: sale.customerData?.address || '',
    city: sale.customerData?.city || '',
    postalCode: sale.customerData?.postalCode || '',
    province: sale.customerData?.province || '',
    country: sale.customerData?.country || 'Argentina'
  },
  subtotal: Number(sale.subtotal) || 0,
  discount: sale.discount != null ? Number(sale.discount) : 0,
  discountType: sale.discountType || 'percentage',
  discountAmount: Number(sale.discountAmount) || 0,
  totalPrice: Number(sale.totalPrice) || 0,
  paymentMethods: sale.paymentMethods || undefined,
  invoiceOptions: sale.invoiceOptions || undefined,
  invoiceData: sale.invoiceData || undefined,
  creditNoteData: sale.creditNoteData || undefined
});

const mapPhysicalSaleToShippingLabel = (sale) => ({
  orderNumber: sale.orderNumber,
  date: toDateValue(sale.date),
  nombre: getCustomerNameFromSale(sale),
  telefono: sale.customerData?.phone || '',
  domicilio: sale.customerData?.address || '',
  localidad: sale.customerData?.city || '',
  codigoPostal: sale.customerData?.postalCode || '',
  provincia: sale.customerData?.province || '',
  pais: sale.customerData?.country || 'Argentina',
  informacionAdicional: withConsignmentNote(sale.customerData?.additionalInfo, isConsignmentSale(sale)),
  cartItems: sale.products || []
});

const mapPhysicalSaleToRemito = (sale, remitoNumber = '') => ({
  orderNumber: sale.orderNumber,
  remitoNumber,
  date: toDateValue(sale.date),
  products: (sale.products || []).map(item => ({
    name: item.name || item.model,
    model: item.model,
    price: Number(item.price) || 0,
    quantity: Number(item.quantity) || 0
  })),
  customerData: {
    name: getCustomerNameFromSale(sale),
    cuit: sale.customerData?.cuit || '',
    phone: sale.customerData?.phone || '',
    address: sale.customerData?.address || ''
  },
  subtotal: Number(sale.subtotal) || 0,
  discount: sale.discount != null ? Number(sale.discount) : 0,
  discountType: sale.discountType || 'percentage',
  discountAmount: Number(sale.discountAmount) || 0,
  totalPrice: Number(sale.totalPrice) || 0,
  paymentMethods: sale.paymentMethods || undefined,
  invoiceOptions: sale.invoiceOptions || undefined,
  invoiceData: sale.invoiceData || undefined,
  observaciones: withConsignmentNote(
    sale.customerData?.additionalInfo || sale.observaciones || '',
    isConsignmentSale(sale)
  )
});

const validatePuntoVenta = (puntoVenta) => {
  const pv = parseInt(puntoVenta);
  if (isNaN(pv) || pv < 1 || pv > 9999) {
    return false;
  }
  return true;
};

const getInvoiceTypeName = (type) => {
  const types = {
    'facturaA': 'Factura A',
    'facturaB': 'Factura B',
    'facturaC': 'Factura C',
    'remito': 'Remito'
  };
  return types[type] || type;
};

// ===========================
// COMPONENTES AUXILIARES
// ===========================

const AfipProgressModal = ({ isVisible, progress, error }) => {
  if (!isVisible) return null;

  return (
    <div className="afip-progress-modal">
      <div className="afip-progress-content">
        <div className="afip-header">
          <h3>🏛️ Procesando con AFIP</h3>
        </div>

        {error ? (
          <div className="afip-error">
            <h4>❌ Error en AFIP</h4>
            <p>{error}</p>
          </div>
        ) : (
          <div className="afip-progress">
            <div className="progress-spinner"></div>
            <p>{progress || 'Procesando...'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SalesSummary = React.memo(({ selectedProducts, calculations, paymentMethods, invoiceOptions }) => {
  const selectedPayments = useMemo(
    () => formatPaymentMethods(paymentMethods, ''),
    [paymentMethods]
  );

  return (
    <div className="sales-summary-optimized">
      <div className="summary-section">
        <h4>📊 Resumen de Venta</h4>
        <div className="summary-item">
          <span>Productos:</span>
          <span>{selectedProducts.length}</span>
        </div>
        <div className="summary-item">
          <span>Subtotal:</span>
          <span>${formatNumber(calculations.subtotal)}</span>
        </div>
        {calculations.discountAmount > 0 && (
          <div className="summary-item discount">
            <span>Descuento:</span>
            <span>-${formatNumber(calculations.discountAmount)}</span>
          </div>
        )}
        <div className="summary-item total">
          <span>Total:</span>
          <span>${formatNumber(calculations.finalTotal)}</span>
        </div>
      </div>

      {selectedPayments && (
        <div className="summary-section">
          <h4>💳 Medio de Pago</h4>
          <p>{selectedPayments}</p>
        </div>
      )}

      {invoiceOptions.generateInvoice && (
        <div className="summary-section invoice-summary">
          <h4>🏛️ Facturación AFIP</h4>
          <p>Tipo: {getInvoiceTypeName(invoiceOptions.invoiceType)}</p>
          <p>Punto de Venta: {String(invoiceOptions.puntoVenta).padStart(4, '0')}</p>
          {invoiceOptions.emailAddress && invoiceOptions.emailAddress.includes('@') && (
            <p>📧 Envío: {invoiceOptions.emailAddress}</p>
          )}
        </div>
      )}
    </div>
  );
});

const CuitInput = ({ value, onChange, onBlur, onKeyDown, required = false, placeholder, isLoading = false, highlighted = false }) => (
  <div className={`cuit-input-container ${highlighted ? 'cuit-input-container-highlighted' : ''}`}>
    {highlighted && (
      <label className="cuit-input-highlighted-label">
        ⭐ CUIT <span className="cuit-input-highlighted-hint"></span>
      </label>
    )}
    <div className="cuit-input-wrapper">
      <input
        type="text"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "CUIT/DNI (ej: 20-12345678-9)"}
        className={`local-sales-customer-input ${required ? 'required-field' : ''} ${highlighted ? 'cuit-highlighted-input' : ''}`}
        maxLength="15"
        inputMode="numeric"
        autoComplete="off"
      />
      {isLoading && (
        <span className="validation-spinner" aria-label="Consultando AFIP">
          ⏳
        </span>
      )}
    </div>
  </div>
);

const EmailInput = ({ value, onChange, highlighted = false }) => (
  <div className={`email-input-container ${highlighted ? 'email-input-container-highlighted' : ''}`}>
    {highlighted && (
      <label className="email-input-highlighted-label">
        📧 Email <span className="email-input-highlighted-hint">(recomendado · factura o presupuesto)</span>
      </label>
    )}
    <input
      type="email"
      placeholder={highlighted ? "ejemplo@correo.com" : "Email para factura, presupuesto o aviso (opcional)"}
      value={value}
      onChange={onChange}
      className={`local-sales-customer-input ${highlighted ? 'email-highlighted-input' : ''}`}
      autoComplete="email"
    />
  </div>
);

const ExtraAddressFields = ({ customerData, onChange, idPrefix = 'ls-extra' }) => (
  <div className="optional-extra-address-fields">
    <div className="basic-info-field extra-address-field-full">
      <label htmlFor={`${idPrefix}-address`} className="basic-info-field-label">
        Dirección
      </label>
      <input
        id={`${idPrefix}-address`}
        type="text"
        name="address"
        placeholder="Calle, número, piso, depto…"
        value={customerData.address || ''}
        onChange={onChange}
        title={customerData.address || ''}
        className="local-sales-customer-input"
      />
    </div>

    <div className="basic-info-field">
      <label htmlFor={`${idPrefix}-city`} className="basic-info-field-label">
        Ciudad
      </label>
      <input
        id={`${idPrefix}-city`}
        type="text"
        name="city"
        placeholder="Ciudad"
        value={customerData.city || ''}
        onChange={onChange}
        title={customerData.city || ''}
        className="local-sales-customer-input"
      />
    </div>

    <div className="basic-info-field">
      <label htmlFor={`${idPrefix}-province`} className="basic-info-field-label">
        Provincia
      </label>
      <input
        id={`${idPrefix}-province`}
        type="text"
        name="province"
        placeholder="Provincia"
        value={customerData.province || ''}
        onChange={onChange}
        title={customerData.province || ''}
        className="local-sales-customer-input"
      />
    </div>

    <div className="basic-info-field">
      <label htmlFor={`${idPrefix}-country`} className="basic-info-field-label">
        País
      </label>
      <input
        id={`${idPrefix}-country`}
        type="text"
        name="country"
        placeholder="País"
        value={customerData.country || ''}
        onChange={onChange}
        title={customerData.country || ''}
        className="local-sales-customer-input"
      />
    </div>

    <div className="basic-info-field">
      <label htmlFor={`${idPrefix}-postalCode`} className="basic-info-field-label">
        Código postal
      </label>
      <input
        id={`${idPrefix}-postalCode`}
        type="text"
        name="postalCode"
        placeholder="CP"
        value={customerData.postalCode || ''}
        onChange={onChange}
        title={customerData.postalCode || ''}
        className="local-sales-customer-input"
      />
    </div>

    <div className="basic-info-field extra-address-notes-field">
      <label htmlFor={`${idPrefix}-observaciones`} className="basic-info-field-label">
        Notas internas
      </label>
      <textarea
        id={`${idPrefix}-observaciones`}
        name="observaciones"
        placeholder="Notas (solo gestión interna, no se imprime en factura)"
        value={customerData.observaciones || ''}
        onChange={onChange}
        className="local-sales-customer-input optional-notes-input"
        rows={2}
      />
    </div>
  </div>
);

const PuntoVentaSelector = ({ value, onChange, disabled = false, showNotification, testAfipConnection }) => {
  const [isValidating, setIsValidating] = useState(false);
  const [lastValidated, setLastValidated] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);

  const handleChange = (e) => {
    const newValue = parseInt(e.target.value);
    onChange(newValue);
    setConnectionStatus(null); // Reset status cuando cambia PV

    if (validatePuntoVenta(newValue)) {
      setLastValidated(newValue);
    }
  };

  const testCurrentPV = async (silent = false) => {
    if (!validatePuntoVenta(value)) {
      showNotification('Punto de venta inválido', 'error');
      return;
    }

    setIsValidating(true);
    setConnectionStatus(null);

    const isSilent = typeof silent === 'boolean' && silent;

    try {
      await testAfipConnection();
      setConnectionStatus('success');
      setLastValidated(value);
      if (!isSilent) showNotification(`✅ PV ${String(value).padStart(4, '0')} conectado correctamente`, 'success');
    } catch (error) {
      console.error('Error probando PV:', error);
      setConnectionStatus('error');
      if (!isSilent) showNotification(`❌ Error conectando PV ${String(value).padStart(4, '0')}`, 'error');
    } finally {
      setIsValidating(false);
    }
  };

  // Ya no hacemos test automático al cargar para no mostrar error de AFIP antes de que el usuario intente facturar
  // El usuario puede probar manualmente con el botón 🧪
  // useEffect(() => { if (validatePuntoVenta(value) && !lastValidated) testCurrentPV(true); }, []);

  return (
    <div className="punto-venta-selector-enhanced">
      <label>Punto de Venta AFIP:</label>
      <div className="pv-input-container">
        <select
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={`punto-venta-select ${!validatePuntoVenta(value) ? 'invalid' : ''}`}
        >
          <option value={1}>0001</option>
          <option value={2}>0002</option>
          <option value={3}>0003</option>
          <option value={4}>0004</option>
          <option value={5}>0005</option>
          <option value={6}>0006</option>
          <option value={7}>0007</option>
          <option value={8}>0008</option>
          <option value={9}>0009</option>
          <option value={10}>0010</option>
          <option value={11}>0011</option>
          <option value={12}>0012</option>
          <option value={13}>0013</option>
          <option value={14}>0014</option>
          <option value={15}>0015</option>
        </select>

        <button
          type="button"
          onClick={testCurrentPV}
          disabled={disabled || isValidating || !validatePuntoVenta(value)}
          className={`test-pv-button ${connectionStatus || ''}`}
          title={`Probar conexión AFIP con PV ${String(value).padStart(4, '0')}`}
        >
          {isValidating ? '🔄' : connectionStatus === 'success' ? '✅' : connectionStatus === 'error' ? '❌' : '🧪'}
        </button>
      </div>

      <div className="pv-info">
        <small className="pv-info-main-text">
          📍 PV {String(value).padStart(4, '0')} seleccionado
          {connectionStatus === 'success' && <span className="pv-info-status-success"> ✅ Conectado</span>}
          {connectionStatus === 'error' && <span className="pv-info-status-error"> ❌ Error conexión</span>}
        </small>
        {!validatePuntoVenta(value) && (
          <small className="pv-info-validation-warning">
            ⚠️ Punto de venta debe ser entre 1 y 9999
          </small>
        )}
      </div>
    </div>
  );
};

// ===========================
// COMPONENTE PRINCIPAL
// ===========================
const LocalSales = () => {
  const navigate = useNavigate();
  // Estados principales
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerData, setCustomerData] = useState({
    name: '',
    cuit: '',
    documentType: '99',
    observaciones: '',
    ivaCondition: '5',
    phone: '',
    email: '',
    address: '',
    city: '',
    province: '',
    country: 'Argentina',
    postalCode: ''
  });
  const [showExtraAddressFields, setShowExtraAddressFields] = useState(false);
  const [frequentCustomers, setFrequentCustomers] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientSearchRef = useRef(null);
  const [discount, setDiscount] = useState({ type: 'percentage', value: 0 });
  const [isCuitLookupLoading, setIsCuitLookupLoading] = useState(false);

  // Estados para medios de pago - un solo método (incluye consignación)
  const [paymentMethods, setPaymentMethods] = useState(emptyPaymentMethods);

  // Estados para facturación con PV dinámico
  const [invoiceOptions, setInvoiceOptions] = useState({
    generateInvoice: true,
    invoiceType: 'facturaC',
    sendByEmail: false,
    emailAddress: '',
    puntoVenta: 8
  });

  // Estados de UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [invoiceProgress, setInvoiceProgress] = useState('');
  const [invoiceError, setInvoiceError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saleInfo, setSaleInfo] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '', show: false });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [quantityInputs, setQuantityInputs] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [recentSales, setRecentSales] = useState([]);
  const [recentSalesLoading, setRecentSalesLoading] = useState(false);
  const [recentMenuFor, setRecentMenuFor] = useState(null);
  const [showDailySales, setShowDailySales] = useState(false);
  const [showRemitoModal, setShowRemitoModal] = useState(false);
  const [remitoSaleInfo, setRemitoSaleInfo] = useState(null);
  const [quoteValidUntil, setQuoteValidUntil] = useState(() => toDateInputValue(addDaysToDate(new Date(), DEFAULT_QUOTE_VALID_DAYS)));
  const [quoteCurrency, setQuoteCurrency] = useState(DEFAULT_QUOTE_CURRENCY);
  const [quoteDestination, setQuoteDestination] = useState(DEFAULT_QUOTE_DESTINATION);
  const [quotePaymentTerms, setQuotePaymentTerms] = useState(DEFAULT_QUOTE_PAYMENT);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteIncludeImages, setQuoteIncludeImages] = useState(false);
  const [quoteIncludeDescription, setQuoteIncludeDescription] = useState(false);
  const [showQuoteConfirm, setShowQuoteConfirm] = useState(false);
  const [isSavingQuote, setIsSavingQuote] = useState(false);
  const [isSendingQuoteEmail, setIsSendingQuoteEmail] = useState(false);
  const quoteReadOnly = isViewerFromStorage();

  const searchInputRef = useRef(null);
  const recentMenuRef = useRef(null);

  // Clientes frecuentes: ventas físicas confirmadas + presupuestos enviados
  useEffect(() => {
    let cancelled = false;
    const loadFrequentCustomers = async () => {
      try {
        const ventasQ = query(
          collection(db, 'ventas_fisicas'),
          where('isConfirmed', '==', true),
          orderBy('date', 'desc'),
          limit(80)
        );
        const quotesQ = query(
          collection(db, 'presupuestos'),
          orderBy('date', 'desc'),
          limit(80)
        );
        const [ventasSnap, quotesSnap] = await Promise.all([
          getDocs(ventasQ),
          getDocs(quotesQ).catch((err) => {
            console.error('Error cargando clientes de presupuestos:', err);
            return { docs: [] };
          })
        ]);
        if (cancelled) return;
        const byKey = new Map();
        ventasSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.isConfirmed !== true) return;
          ingestFrequentCustomer(byKey, data, 'venta');
        });
        quotesSnap.docs.forEach((d) => {
          const data = d.data() || {};
          const sent = data.status === 'sent' || !!(data.emailedTo || '').trim();
          if (!sent) return;
          ingestFrequentCustomer(byKey, data, 'presupuesto');
        });
        setFrequentCustomers(Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        if (!cancelled) console.error('Error cargando clientes frecuentes:', err);
      }
    };
    loadFrequentCustomers();
    return () => { cancelled = true; };
  }, []);

  // ===========================
  // CUSTOM HOOKS Y CÁLCULOS
  // ===========================

  // Cálculos optimizados con useMemo
  const calculations = useMemo(() => {
    const subtotal = selectedProducts.reduce((total, item) => {
      const itemTotal = parseFloat(item.price) * parseInt(item.quantity, 10);
      return total + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);

    let discountAmount = 0;
    if (discount.type === 'percentage') {
      discountAmount = (subtotal * discount.value) / 100;
    } else if (discount.type === 'fixed') {
      discountAmount = Math.min(discount.value, subtotal);
    }

    const finalTotal = Math.max(0, subtotal - discountAmount);
    return { subtotal, discountAmount, finalTotal };
  }, [selectedProducts, discount]);

  // Destacar el campo de email cuando el CUIT/nombre ya está cargado pero falta email
  const shouldHighlightEmail = useMemo(() => {
    const cuitDigits = (customerData.cuit || '').replace(/\D/g, '');
    const hasCuitOrDni = cuitDigits.length >= 8;
    const hasName = !!(customerData.name || '').trim();
    const emailFilled = !!(invoiceOptions.emailAddress || customerData.email || '').trim();
    return (hasCuitOrDni || hasName) && !emailFilled;
  }, [
    invoiceOptions.emailAddress,
    customerData.email,
    customerData.cuit,
    customerData.name
  ]);

  const filteredFrequentClients = useMemo(() => {
    const term = (clientSearchTerm || '').trim().toLowerCase();
    if (!term) return frequentCustomers.slice(0, 10);
    const cuitDigits = term.replace(/\D/g, '');
    return frequentCustomers.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(term)) ||
        (c.email && c.email.toLowerCase().includes(term)) ||
        (c.cuit && (c.cuit.toLowerCase().includes(term) || c.cuit.replace(/\D/g, '').includes(cuitDigits)))
    ).slice(0, 10);
  }, [frequentCustomers, clientSearchTerm]);

  const selectFrequentClient = useCallback((client) => {
    setCustomerData((prev) => ({
      ...prev,
      name: client.name || prev.name,
      cuit: client.cuit || prev.cuit,
      documentType: client.documentType || prev.documentType,
      email: client.email || prev.email,
      phone: client.phone || prev.phone,
      ivaCondition: client.ivaCondition || prev.ivaCondition,
      address: client.address || prev.address,
      city: client.city || prev.city,
      province: client.province || prev.province,
      country: client.country || prev.country,
      postalCode: client.postalCode || prev.postalCode
    }));
    if (client.email) {
      setInvoiceOptions((prev) => ({ ...prev, emailAddress: client.email }));
    }
    setClientSearchTerm('');
    setShowClientDropdown(false);
  }, []);

  // ===========================
  // FUNCIONES PRINCIPALES
  // ===========================

  // Imprimir ticket en ventana nueva; estilos autocontenidos para que la vista previa funcione en todos los equipos
  const printTicketInNewWindow = useCallback((ticketWrapperEl) => {
    if (!ticketWrapperEl || !ticketWrapperEl.innerHTML) return;
    const criticalTicketCss = `
*{box-sizing:border-box}
html,body{margin:0;padding:0;background:#fff;color:#1a1a1a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.ticket-wrapper{width:100%;max-width:100%;font-family:Arial,sans-serif;background:#fff;visibility:visible}
.ticket-content{background:#fff;padding:0;color:#1a1a1a;font-size:12px;line-height:1.35;display:flex;flex-direction:column;min-height:0;visibility:visible}
.ticket-content-inner{padding:16px;flex:1;display:flex;flex-direction:column;visibility:visible}
.ticket-type-banner{display:flex;align-items:center;justify-content:center;width:100%;font-size:18px;font-weight:700;padding:10px 16px;background:#b8d4e8;color:#1a1a1a;border-bottom:2px solid #000}
.ticket-sep{border:none;border-top:1px solid #000;margin:10px 0}
.ticket-header-three-cols{display:grid;grid-template-columns:1fr auto 1fr;gap:12px;font-size:10px;align-items:center;margin-bottom:0}
.ticket-col-emisor{padding:6px 0;min-width:0}
.ticket-logo-small{max-width:192px;max-height:36px;width:auto;height:auto;display:block;margin-bottom:6px;object-fit:contain}
.ticket-col-emisor p{margin:2px 0;line-height:1.35;color:#333}
.ticket-col-tipo{display:flex;align-items:center;justify-content:center;min-width:0}
.ticket-tipo-box{background:#e0e0e0;color:#000;width:64px;min-height:72px;padding:8px 4px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;flex-shrink:0;border:1px solid #bbb;border-radius:6px}
.ticket-tipo-letra{display:block;font-size:28px;font-weight:700;line-height:1}
.ticket-tipo-codigo{display:block;font-size:11px;margin-top:4px;font-weight:600}
.ticket-tipo-monotributista{display:block;font-size:9px;margin-top:2px}
.ticket-col-datos{text-align:right;min-width:0}
.ticket-col-datos p{margin:2px 0;line-height:1.35;color:#333}
.ticket-factura-title{font-weight:700;font-size:13px;margin-bottom:4px!important}
.ticket-order{margin-bottom:0}
.order-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;margin-bottom:4px;font-size:11px;align-items:baseline}
.order-row strong{grid-column:1}
.order-row span{grid-column:2;text-align:right}
.ticket-customer,.ticket-payment,.ticket-invoice{margin:10px 0}
.customer-row,.payment-row,.invoice-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;align-items:center;margin:5px 0;font-size:11px}
.products-header2{display:grid;grid-template-columns:1fr 3fr 1.2fr 1.2fr;gap:8px;padding:6px 8px;font-weight:700;font-size:11px;background:#b8d4e8;border:1px solid #000;margin-bottom:0}
.product-row{display:grid;grid-template-columns:1fr 3fr 1.2fr 1.2fr;gap:8px;padding:6px 8px;font-size:11px;border-bottom:1px solid #000}
.ticket-summary{margin-top:15px}
.summary-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;margin-bottom:5px;align-items:baseline}
.summary-row span:first-child{grid-column:1}
.summary-row span:last-child{grid-column:2;text-align:right}
.total-row{padding-top:10px;border-top:2px solid #ddd;font-size:16px}
.total-row strong{grid-column:1 / -1;text-align:right;color:#1a1a1a;font-size:14px}
.ticket-footer-afip{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;text-align:left;background:#fff;padding-left:20px;padding-right:20px}
.ticket-qr img{display:block;border:1px solid #ddd}
.ticket-qr-label{font-size:10px;margin:0;color:#666}
.ticket-thanks{margin-top:6px!important;font-weight:bold}
@media print{.ticket-actions,.print-btn{display:none!important}body,body *{visibility:visible}body{margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}body .ticket-wrapper{display:block!important;visibility:visible!important;position:relative!important;left:auto;top:auto;width:100%!important;max-width:210mm!important;margin:0;padding:0;background:#fff!important}body .ticket-wrapper *{visibility:visible!important}body .ticket-wrapper .ticket-header-three-cols{display:grid!important}}
`;
    let html = ticketWrapperEl.innerHTML;
    const logoImg = ticketWrapperEl.querySelector('.ticket-logo-small');
    const logoSrc = logoImg?.getAttribute('src');
    if (logoSrc && !logoSrc.startsWith('data:')) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      let resolved = false;
      const runPrint = () => {
        if (resolved) return;
        resolved = true;
        openAndPrint();
      };
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          c.getContext('2d').drawImage(img, 0, 0);
          html = html.split(logoSrc).join(c.toDataURL('image/png'));
        } catch (_) {}
        runPrint();
      };
      img.onerror = runPrint;
      img.src = logoSrc;
      setTimeout(runPrint, 1500);
    } else {
      openAndPrint();
    }
    function openAndPrint() {
      const win = window.open('', '_blank');
      if (!win) {
        setNotification({ message: 'Permití ventanas emergentes para imprimir.', type: 'error', show: true });
        setTimeout(() => setNotification((n) => ({ ...n, show: false })), 3000);
        return;
      }
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Comprobante</title><style>${criticalTicketCss}</style></head><body>${html}</body></html>`);
      win.document.close();
      let printed = false;
      const doPrint = () => {
        if (printed) return;
        printed = true;
        win.focus();
        win.print();
        setTimeout(() => win.close(), 400);
      };
      const waitThenPrint = () => {
        if (win.document.readyState !== 'complete') { win.onload = waitThenPrint; return; }
        const imgs = win.document.querySelectorAll('img');
        if (Array.from(imgs).every((i) => i.complete)) {
          setTimeout(() => { win.focus(); setTimeout(doPrint, 300); }, 100);
          return;
        }
        let waited = 0;
        const check = () => {
          if (Array.from(imgs).every((i) => i.complete) || waited >= 5000) {
            setTimeout(() => { win.focus(); setTimeout(doPrint, 300); }, 100);
            return;
          }
          waited += 200;
          setTimeout(check, 200);
        };
        setTimeout(check, 200);
      };
      win.onload = waitThenPrint;
      setTimeout(waitThenPrint, 2500);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const productsCollection = await getDocs(collection(db, 'products'));
      const productsData = productsCollection.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        stock: Number(doc.data().stock) || 0,
        excludeFromStock: doc.data().excludeFromStock ?? false
      }));
      setProducts(productsData);
    } catch (error) {
      console.error("Error fetching products:", error);
      showNotification('Error al cargar productos', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Listener para cerrar modales con tecla Escape
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showRemitoModal) {
          setShowRemitoModal(false);
          setRemitoSaleInfo(null);
        } else if (recentMenuFor) {
          setRecentMenuFor(null);
        } else if (showModal) {
          // No cerrar el modal principal de venta registrada con Escape
          // para evitar cerrar accidentalmente después de una venta
          return;
        } else if (showConfirmDialog) {
          setShowConfirmDialog(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showConfirmDialog, showModal, showRemitoModal, recentMenuFor]);

  // Auto-save cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedProducts.length > 0) {
        try {
          const saveData = {
            selectedProducts,
            customerData,
            discount,
            paymentMethods,
            invoiceOptions,
            timestamp: Date.now()
          };
          localStorage.setItem('localsales_backup', JSON.stringify(saveData));
          setHasUnsavedChanges(true);
          setTimeout(() => setHasUnsavedChanges(false), 2000);
        } catch (error) {
          console.warn('No se pudo guardar backup:', error);
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedProducts, customerData, discount, paymentMethods, invoiceOptions]);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type, show: true });
  };

  const hideNotification = () => {
    setNotification({ message: '', type: '', show: false });
  };

  const loadRecentSales = useCallback(async () => {
    setRecentSalesLoading(true);
    try {
      const { start: todayStart, end: todayEnd } = getTodayLocalBounds();
      const ventasRef = collection(db, 'ventas_fisicas');
      const recentQuery = query(
        ventasRef,
        where('isConfirmed', '==', true),
        where('date', '>=', todayStart),
        where('date', '<=', todayEnd),
        orderBy('date', 'desc'),
        limit(DAILY_SALES_QUERY_LIMIT)
      );
      const snapshot = await getDocs(recentQuery);
      setRecentSales(
        snapshot.docs.map((saleDoc) => ({
          id: saleDoc.id,
          ...saleDoc.data()
        }))
      );
    } catch (error) {
      console.error('Error cargando ventas del día:', error);
      showNotification('Error al cargar ventas del día', 'error');
    } finally {
      setRecentSalesLoading(false);
    }
  }, []);

  /** Solo consultar Firestore al abrir el panel (no al entrar al POS). Tras cada venta sí se refresca. */
  useEffect(() => {
    if (!showDailySales) return;
    loadRecentSales();
  }, [showDailySales, loadRecentSales]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recentMenuRef.current && !recentMenuRef.current.contains(event.target)) {
        setRecentMenuFor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNextRemitoNumber = useCallback(async () => {
    const counterRef = doc(db, 'counters', 'remitoCounter');
    const newNumber = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const current = snap.exists() ? Number(snap.data().currentRemitoNumber) || 0 : 0;
      const next = current + 1;
      tx.set(counterRef, { currentRemitoNumber: next }, { merge: true });
      return next;
    });
    return `R-${String(newNumber).padStart(6, '0')}`;
  }, []);

  const openRecentSaleTicket = useCallback((sale) => {
    setRecentMenuFor(null);
    setSaleInfo(mapPhysicalSaleToPrintInfo(sale));
    setShowModal(true);
  }, []);

  const printRecentShippingLabel = useCallback((sale) => {
    setRecentMenuFor(null);
    printShippingLabel(mapPhysicalSaleToShippingLabel(sale), {
      onPopupBlocked: () => showNotification('Permití ventanas emergentes para imprimir el rótulo.', 'error')
    });
  }, []);

  const openRecentRemito = useCallback(async (sale) => {
    setRecentMenuFor(null);
    let remitoNumber = sale.remitoNumber || '';
    try {
      if (!remitoNumber) {
        remitoNumber = await getNextRemitoNumber();
        const saleRef = doc(db, 'ventas_fisicas', sale.id);
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(saleRef);
          if (!snap.exists()) return;
          if (!snap.data().remitoNumber) {
            tx.update(saleRef, { remitoNumber });
          } else {
            remitoNumber = snap.data().remitoNumber;
          }
        });
        setRecentSales(prev => prev.map(item =>
          item.id === sale.id ? { ...item, remitoNumber } : item
        ));
      }
    } catch (error) {
      console.error('Error generando número de remito:', error);
      showNotification('No se pudo generar el número de remito.', 'error');
      return;
    }
    setRemitoSaleInfo(mapPhysicalSaleToRemito(sale, remitoNumber));
    setShowRemitoModal(true);
  }, [getNextRemitoNumber]);

  const handleKeyPress = (event, product, quantity) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addProductToSale(product, quantity);
    } else if (event.key === "Tab") {
      addProductToSale(product, quantity);
    }
  };

  const catalogStockOf = (productId, fallbackProduct) => {
    const live = products.find((p) => p.id === productId);
    const source = live || fallbackProduct;
    if (!source) return 0;
    return Number(source.stock) || 0;
  };

  const willGoNegative = (product, requestedQuantity, currentInCart = 0) => {
    if (!product || product.excludeFromStock === true) return false;
    return catalogStockOf(product.id, product) < (currentInCart + requestedQuantity);
  };

  const oversellLines = selectedProducts
    .map((item) => {
      const live = products.find((p) => p.id === item.id) || item;
      if (live.excludeFromStock === true) return null;
      const stock = Number(live.stock) || 0;
      if (stock >= item.quantity) return null;
      return {
        id: item.id,
        model: item.model,
        quantity: item.quantity,
        stock,
        resultingStock: stock - item.quantity
      };
    })
    .filter(Boolean);

  const addProductToSale = (product, quantity) => {
    const parsedQuantity = Number(quantity);
    if (parsedQuantity <= 0 || isNaN(parsedQuantity)) {
      showNotification('Cantidad debe ser mayor a 0', 'warning');
      return;
    }

    const currentInCart = selectedProducts.find((item) => item.id === product.id)?.quantity || 0;
    if (willGoNegative(product, parsedQuantity, currentInCart)) {
      const resulting = catalogStockOf(product.id, product) - (currentInCart + parsedQuantity);
      showNotification(
        `${product.model}: sin stock suficiente (${catalogStockOf(product.id, product)}). Quedará en ${resulting}.`,
        'warning'
      );
    }

    setSelectedProducts(prevSelectedProducts => {
      const existingProduct = prevSelectedProducts.find(item => item.id === product.id);

      if (existingProduct) {
        const newQuantity = existingProduct.quantity + parsedQuantity;
        return prevSelectedProducts.map(item =>
          item.id === product.id
            ? { ...item, quantity: newQuantity }
            : item
        );
      }
      return [...prevSelectedProducts, { ...product, quantity: parsedQuantity }];
    });

    setQuantityInputs(prev => ({ ...prev, [product.id]: '' }));
    setSearchTerm('');
    showNotification(`${product.model} agregado correctamente`, 'success');

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeProductFromSale = (productId) => {
    setSelectedProducts(prevSelectedProducts =>
      prevSelectedProducts.filter(item => item.id !== productId)
    );
    showNotification('Producto eliminado', 'info');
  };

  const updateProductQuantity = (productId, newQuantity) => {
    const parsedQuantity = Number(newQuantity);
    if (parsedQuantity <= 0 || isNaN(parsedQuantity)) {
      removeProductFromSale(productId);
      return;
    }

    const product = products.find(p => p.id === productId);
    if (product && willGoNegative(product, parsedQuantity, 0)) {
      showNotification(
        `${product.model}: stock ${catalogStockOf(product.id, product)}. Quedará en ${catalogStockOf(product.id, product) - parsedQuantity}.`,
        'warning'
      );
    }

    setSelectedProducts(prevSelected =>
      prevSelected.map(item =>
        item.id === productId ? { ...item, quantity: parsedQuantity } : item
      )
    );
  };

  const handlePriceChange = (productId, newPrice) => {
    const parsedPrice = parseFloat(newPrice);
    if (isNaN(parsedPrice) || parsedPrice < 0) return;

    setSelectedProducts(prevSelected =>
      prevSelected.map(item => item.id === productId ? { ...item, price: parsedPrice } : item)
    );
  };

  // FUNCIÓN MODIFICADA: Solo permite seleccionar un método de pago
  const handlePaymentMethodChange = (method) => {
    const newPaymentMethods = emptyPaymentMethods();
    newPaymentMethods[method] = true;
    setPaymentMethods(newPaymentMethods);
    if (method === 'consignacion') {
      setInvoiceOptions(prev => ({ ...prev, generateInvoice: false }));
    }
  };

  const handleInvoiceOptionChange = (field, value) => {
    setInvoiceOptions(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'generateInvoice' && value && customerData.email && !invoiceOptions.emailAddress) {
      setInvoiceOptions(prev => ({
        ...prev,
        emailAddress: customerData.email
      }));
    }
  };

  const getOrderNumber = async () => {
    const counterRef = doc(db, 'counters', 'fisicalCounter');
    try {
      const newOrderNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        if (!counterDoc.exists()) {
          throw new Error('Fisical counter document does not exist!');
        }
        const newOrderNumber = counterDoc.data().currentOrderNumber + 1;
        transaction.update(counterRef, { currentOrderNumber: newOrderNumber });
        return newOrderNumber;
      });
      return newOrderNumber;
    } catch (error) {
      console.error("Transaction failed: ", error);
      showNotification('Error generando número de orden', 'error');
      return null;
    }
  };

  const getQuoteNumber = async () => {
    const counterRef = doc(db, 'counters', 'presupuestoCounter');
    try {
      const newQuoteNumber = await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        if (!counterDoc.exists()) {
          transaction.set(counterRef, { currentOrderNumber: 1 });
          return 1;
        }
        const next = (Number(counterDoc.data().currentOrderNumber) || 0) + 1;
        transaction.update(counterRef, { currentOrderNumber: next });
        return next;
      });
      return newQuoteNumber;
    } catch (error) {
      console.error('Transaction failed: ', error);
      showNotification('Error generando número de presupuesto', 'error');
      return null;
    }
  };

  const buildQuotePayload = (quoteNumber) => {
    const { subtotal, discountAmount, finalTotal } = calculations;
    const quoteEmail = (
      (customerData.email || '').trim() ||
      (invoiceOptions.emailAddress || '').trim()
    );
    const validUntil = parseLocalDateInput(quoteValidUntil) || addDaysToDate(new Date(), DEFAULT_QUOTE_VALID_DAYS);
    const customerDataToSave = {
      ...customerData,
      name: (customerData.name || '').trim() || 'Consumidor Final',
      ...(quoteEmail.includes('@') ? { email: quoteEmail } : {})
    };
    return {
      quoteNumber,
      orderNumber: quoteNumber,
      date: new Date(),
      validUntil,
      currency: (quoteCurrency || '').trim() || DEFAULT_QUOTE_CURRENCY,
      destinationProvince: (quoteDestination || '').trim() || DEFAULT_QUOTE_DESTINATION,
      paymentTerms: (quotePaymentTerms || '').trim() || DEFAULT_QUOTE_PAYMENT,
      products: selectedProducts.map((product) => ({
        id: product.id,
        model: product.model,
        title: product.model,
        price: Number(product.price),
        quantity: Number(product.quantity),
        images: product.images || [],
        description: product.description || product.descripcion || ''
      })),
      customerData: customerDataToSave,
      subtotal,
      discount: discount.value,
      discountType: discount.type,
      discountAmount,
      totalPrice: finalTotal,
      paymentMethods: Object.values(paymentMethods).some(Boolean) ? paymentMethods : null,
      invoiceOptions: {
        emailAddress: quoteEmail.includes('@') ? quoteEmail : '',
        puntoVenta: Number(invoiceOptions.puntoVenta) || 8
      },
      puntoVenta: Number(invoiceOptions.puntoVenta) || 8,
      notes: (quoteNotes || '').trim(),
      includeImages: quoteIncludeImages === true,
      includeDescription: quoteIncludeDescription === true,
      status: 'saved',
      documentKind: 'presupuesto',
      isQuote: true,
      createdAt: new Date()
    };
  };

  const sendSavedQuoteEmail = async (quote) => {
    const emailTo = (
      quote?.customerData?.email ||
      quote?.invoiceOptions?.emailAddress ||
      ''
    ).trim();
    if (!emailTo.includes('@')) {
      showNotification('Cargá un email de cliente para enviar el presupuesto.', 'warning');
      return false;
    }
    let url = '';
    try {
      url = getCloudFunctionUrl('sendLocalSaleQuoteEmail');
    } catch {
      url = '';
    }
    if (!url) {
      showNotification('URL de envío de presupuesto no configurada.', 'error');
      return false;
    }
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      showNotification('Iniciá sesión para enviar el presupuesto por email.', 'error');
      return false;
    }
    const res = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ quoteId: quote.id, email: emailTo })
    }, 90000);
    const result = await res.json().catch(() => ({}));
    if (!res.ok || !result.success) {
      throw new Error(result.message || 'No se pudo enviar el presupuesto.');
    }
    return true;
  };

  const openQuoteConfirm = () => {
    if (quoteReadOnly) {
      showNotification('El rol viewer no puede crear presupuestos.', 'warning');
      return;
    }
    if (selectedProducts.length === 0) {
      showNotification('Agregá al menos un producto al presupuesto', 'warning');
      return;
    }
    if (!(customerData.name || '').trim()) {
      showNotification('Completá el nombre del cliente para el presupuesto', 'warning');
      return;
    }
    setShowQuoteConfirm(true);
  };

  const handleQuoteSubmit = async ({ sendEmail }) => {
    if (quoteReadOnly) return;
    setIsSavingQuote(true);
    try {
      const quoteNumber = await getQuoteNumber();
      if (!quoteNumber) return;
      const payload = buildQuotePayload(quoteNumber);
      const ref = await addDoc(collection(db, 'presupuestos'), payload);
      const saved = { ...payload, id: ref.id };
      showNotification(`Presupuesto #${quoteNumber} guardado`, 'success');
      if (sendEmail) {
        setIsSendingQuoteEmail(true);
        try {
          await sendSavedQuoteEmail(saved);
          showNotification(`Presupuesto enviado a ${saved.customerData?.email || saved.invoiceOptions?.emailAddress}`, 'success');
        } catch (emailError) {
          console.error(emailError);
          showNotification(emailError.message || 'El presupuesto se guardó pero no se pudo enviar el email. Reintentá desde Clientes.', 'warning');
        } finally {
          setIsSendingQuoteEmail(false);
        }
      }
      setShowQuoteConfirm(false);
      setQuoteNotes('');
      setQuoteIncludeImages(false);
      setQuoteIncludeDescription(false);
      setSelectedProducts([]);
      setQuantityInputs({});
      localStorage.removeItem('localsales_backup');
    } catch (error) {
      console.error('Error guardando presupuesto:', error);
      showNotification('Error al guardar el presupuesto', 'error');
    } finally {
      setIsSavingQuote(false);
    }
  };

  // ===========================
  // FUNCIONES AFIP
  // ===========================

  const generateInvoiceWithAfip = async (saleData) => {
    try {
      const url = getAfipFunctionUrl('generateAfipInvoiceReal');
      if (!url) {
        showNotification('URL de Cloud Functions no configurada. Revise config.', 'error');
        return { success: false, error: 'Configuración de Cloud Functions faltante' };
      }

      setIsGeneratingInvoice(true);
      setInvoiceProgress('Validando datos fiscales...');

      console.log('🚀 AFIP REAL - Iniciando facturación con implementación real...');
      const pv = Number(invoiceOptions.puntoVenta) || 8;
      console.log(`📍 Punto de Venta: ${pv}`);

      if (!saleData?.products?.length) {
        throw new Error('No hay productos en la venta');
      }

      setInvoiceProgress('Preparando datos para AFIP REAL...');

      const emailToSend = (invoiceOptions.emailAddress || saleData.customerData?.email || '').trim();
      const requestPayload = {
        saleData: {
          orderNumber: Number(saleData.orderNumber),
          products: saleData.products.map(product => ({
            id: product.id,
            model: product.model,
            name: product.model,
            price: Number(product.price),
            quantity: Number(product.quantity)
          })),
          customerData: {
            name: saleData.customerData.name || "Consumidor Final",
            email: saleData.customerData.email || "",
            cuit: saleData.customerData.cuit || "",
            cuil: saleData.customerData.cuil || "",
            dni: saleData.customerData.dni || "",
            documentType: saleData.customerData.documentType || "99",
            address: saleData.customerData.address || "",
            city: saleData.customerData.city || "",
            postalCode: saleData.customerData.postalCode || "",
            province: saleData.customerData.province || "",
            country: saleData.customerData.country || "Argentina"
          },
          subtotal: Number(saleData.subtotal),
          discount: saleData.discount != null ? Number(saleData.discount) : 0,
          discountType: saleData.discountType || 'percentage',
          discountAmount: Number(saleData.discountAmount || 0),
          totalPrice: Number(saleData.totalPrice),
          invoiceOptions: {
            sendByEmail: !!emailToSend && emailToSend.includes('@'),
            emailAddress: emailToSend
          }
        },
        invoiceType: invoiceOptions.invoiceType,
        puntoVenta: pv,
        sendByEmail: !!emailToSend && emailToSend.includes('@'),
        emailAddress: emailToSend
      };

      console.log('📤 Datos enviados a AFIP REAL:', JSON.stringify(requestPayload, null, 2));

      setInvoiceProgress('Conectando con AFIP...');

      const response = await fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestPayload)
        },
        120000
      );

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        console.error('📋 Error completo de AFIP REAL:', errorData);

        // Log detalles específicos del error si están disponibles
        if (errorData.debug) {
          console.error('🔍 Detalles del error:', errorData.debug);
          console.error('📍 Punto de venta:', errorData.debug.puntoVenta);
          console.error('🏛️ Ambiente:', errorData.debug.ambiente);
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      setInvoiceProgress('Comprobante AFIP REAL generado exitosamente');
      console.log(`🎯 FACTURA AFIP REAL generada:`, result.data);
      console.log(`📄 Número: ${result.data.invoice.numero}`);
      console.log(`🔢 CAE: ${result.data.invoice.cae}`);
      console.log(`📍 Punto de Venta: ${result.data.invoice.punto_venta}`);

      // Devolver el objeto completo (incluye success, message y data)
      return result;

    } catch (error) {
      console.error('❌ ERROR en facturación AFIP REAL:', error);
      const msg = error.name === 'AbortError' ? 'Timeout en conexión con AFIP. Intente nuevamente.' : error.message;
      setInvoiceError(msg);

      if (error.code === 'functions/invalid-argument') {
        throw new Error('Error de validación en AFIP: ' + error.message);
      } else if (error.code === 'functions/deadline-exceeded' || error.name === 'AbortError') {
        throw new Error('Timeout en conexión con AFIP. Intente nuevamente.');
      } else if (error.code === 'functions/unavailable') {
        throw new Error('AFIP no disponible. Intente más tarde.');
      } else if (error.code === 'functions/internal') {
        throw new Error('Error interno del servidor. Revise configuración AFIP.');
      }

      throw new Error(`Error AFIP REAL: ${msg}`);
    } finally {
      setIsGeneratingInvoice(false);
      setInvoiceProgress('');
    }
  };

  const testAfipConnection = async () => {
    try {
      const url = getAfipFunctionUrl('testAfipConnection');
      if (!url) {
        showNotification('URL de Cloud Functions no configurada. Revise config.', 'error');
        return;
      }
      const pv = Number(invoiceOptions.puntoVenta) || 8;
      console.log(`🔄 Probando conexión AFIP REAL con PV: ${pv}...`);

      const response = await fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ puntoVenta: pv })
        },
        60000
      );

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`✅ Conexión AFIP REAL exitosa en PV ${pv}:`, result.data);

      const testInfo = result.data;
      const notificationMessage = `✅ AFIP REAL: PV ${String(pv).padStart(4, '0')} - ${testInfo.implementation} - Último: ${testInfo.lastVoucher}`;

      showNotification(notificationMessage, 'success');

      if (testInfo.testInvoice) {
        console.log('📄 Factura de prueba generada:', testInfo.testInvoice);
      }
      if (testInfo.capabilities) {
        console.log('🛠️ Capacidades AFIP:', testInfo.capabilities);
      }

      return result.data;
    } catch (error) {
      const msg = error.name === 'AbortError' ? 'Timeout. Intente nuevamente.' : error.message;
      const pv = Number(invoiceOptions.puntoVenta) || 8;
      console.error(`❌ Error probando conexión AFIP en PV ${pv}:`, error);
      showNotification(`❌ AFIP PV ${String(pv).padStart(4, '0')}: ${msg}`, 'error');
      throw error;
    }
  };

  const checkAfipConfiguration = async () => {
    try {
      const url = getAfipFunctionUrl('checkAfipConfig');
      if (!url) {
        showNotification('URL de Cloud Functions no configurada. Revise config.', 'error');
        return;
      }
      console.log('🔍 Verificando configuración AFIP completa...');

      const response = await fetchWithTimeout(
        url,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } },
        30000
      );

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errData = await response.json();
          errorMessage = errData.message || errData.error || errorMessage;
        } catch (_) { }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('🏛️ Configuración AFIP:', result);

      if (result.success) {
        showNotification('Configuración AFIP verificada correctamente', 'success');
      } else {
        showNotification('Problema en configuración AFIP', 'warning');
      }

      return result;
    } catch (error) {
      const msg = error.name === 'AbortError' ? 'Timeout. Intente nuevamente.' : error.message;
      console.error('❌ Error verificando configuración AFIP:', error);
      showNotification(`Error verificando AFIP: ${msg}`, 'error');
      throw error;
    }
  };

  const diagnoseWSAAToken = async () => {
    try {
      const url = getAfipFunctionUrl('diagnoseWSAAToken');
      if (!url) {
        showNotification('URL de Cloud Functions no configurada. Revise config.', 'error');
        return;
      }
      console.log('🔍 Iniciando diagnóstico específico de WSAA Token...');
      showNotification('🔍 Diagnóstico WSAA iniciado...', 'info');

      const response = await fetchWithTimeout(
        url,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        120000
      );

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errData = await response.json();
          errorMessage = errData.message || errData.error || errorMessage;
        } catch (_) { }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('🔍 Diagnóstico WSAA:', result);

      if (result.success) {
        showNotification('✅ Diagnóstico WSAA exitoso - Token obtenido correctamente', 'success');

        // Mostrar detalles del diagnóstico
        const details = result.results.map(r => `${r.step}. ${r.name}: ${r.status}`).join('\n');
        console.log('📋 Detalles del diagnóstico:', details);

        // Si hay token, mostrar información
        const tokenStep = result.results.find(r => r.name === 'Token WSAA');
        if (tokenStep && tokenStep.status === 'OK') {
          console.log('🔑 Token WSAA obtenido:', tokenStep.details);
        }

      } else {
        // Mostrar qué paso falló
        const failedStep = result.results.find(r => r.status === 'ERROR');
        if (failedStep) {
          showNotification(`❌ Diagnóstico WSAA falló en paso ${failedStep.step}: ${failedStep.name}`, 'error');
          console.error('❌ Error en paso:', failedStep);
        } else {
          showNotification('❌ Diagnóstico WSAA falló', 'error');
        }
      }

      return result;
    } catch (error) {
      console.error('❌ Error en diagnóstico WSAA:', error);
      showNotification(`Error en diagnóstico WSAA: ${error.message}`, 'error');
      throw error;
    }
  };

  // ===========================
  // VALIDACIONES
  // ===========================

  const validateSale = () => {
    if (selectedProducts.length === 0) {
      showNotification('Debe agregar al menos un producto', 'warning');
      return false;
    }

    // Verificar que exactamente un método de pago esté seleccionado
    const selectedPaymentMethods = Object.values(paymentMethods).filter(method => method);
    if (selectedPaymentMethods.length === 0) {
      showNotification('Debe seleccionar un medio de pago', 'warning');
      return false;
    }

    if (paymentMethods.consignacion) {
      const name = (customerData.name || '').trim();
      if (!name || name.toLowerCase() === 'consumidor final') {
        showNotification('La consignación requiere el nombre del cliente', 'warning');
        return false;
      }
    }

    if (invoiceOptions.generateInvoice && !paymentMethods.consignacion) {
      if (invoiceOptions.invoiceType === 'facturaA' || invoiceOptions.invoiceType === 'facturaB') {
        if (!customerData.cuit || customerData.cuit.length < 8) {
          showNotification('CUIT válido requerido para este tipo de comprobante', 'warning');
          return false;
        }
      }

      if (!validatePuntoVenta(invoiceOptions.puntoVenta)) {
        showNotification('Punto de venta inválido', 'warning');
        return false;
      }
    }

    for (const item of selectedProducts) {
      const product = products.find(p => p.id === item.id);
      if (!product) {
        showNotification(`Producto no encontrado: ${item.model}`, 'error');
        return false;
      }
    }

    return true;
  };

  // ===========================
  // SUBMIT PRINCIPAL
  // ===========================

  const handleSaleSubmit = async () => {
    if (!validateSale()) return;

    setShowConfirmDialog(false);
    setIsSubmitting(true);

    const orderNumber = await getOrderNumber();
    if (!orderNumber) {
      setIsSubmitting(false);
      return;
    }

    try {
      const batch = writeBatch(db);
      const { subtotal, discountAmount, finalTotal } = calculations;

      const normalizedCustomerName = (customerData.name || '').trim() || 'Consumidor Final';
      const isConsignment = !!paymentMethods.consignacion;
      const additionalInfo = withConsignmentNote(customerData.additionalInfo, isConsignment);
      const consignmentEmail = (
        (customerData.email || '').trim() ||
        (invoiceOptions.emailAddress || '').trim()
      );
      const consignmentEmailValid = consignmentEmail.includes('@');
      const customerDataToSave = {
        ...customerData,
        name: normalizedCustomerName,
        additionalInfo,
        ...(invoiceOptions.emailAddress && { email: invoiceOptions.emailAddress }),
        ...(isConsignment && consignmentEmailValid ? { email: consignmentEmail } : {})
      };
      const saleData = {
        orderNumber,
        date: new Date(),
        products: selectedProducts,
        customerData: customerDataToSave,
        subtotal,
        discount: discount.value,
        discountType: discount.type,
        discountAmount,
        totalPrice: finalTotal,
        paymentMethods: paymentMethods,
        invoiceOptions: isConsignment
          ? {
              ...invoiceOptions,
              generateInvoice: false,
              sendByEmail: consignmentEmailValid,
              emailAddress: consignmentEmailValid ? consignmentEmail : ''
            }
          : invoiceOptions,
        isConfirmed: true,
        isPaid: !isConsignment,
        isConsignment,
        saleType: isConsignment ? 'consignacion' : 'venta',
        environment: 'production'
      };

      let invoiceData = null;
      if (invoiceOptions.generateInvoice && !isConsignment) {
        try {
          const result = await generateInvoiceWithAfip(saleData);

          if (result.success && result.data?.invoice) {
            invoiceData = result.data.invoice;
            saleData.invoiceData = invoiceData;

            if (invoiceData.isSimulated) {
              showNotification('⚠️ Comprobante simulado generado - Certificados AFIP en configuración', 'warning');
            } else {
              showNotification('✅ Comprobante fiscal generado exitosamente con AFIP', 'success');
            }
            if (result.data.emailSent) {
              showNotification(`📧 Comprobante enviado por email a ${result.data.emailTo || invoiceOptions.emailAddress}`, 'success');
            } else if (invoiceOptions.emailAddress && invoiceOptions.emailAddress.includes('@')) {
              if (result.data.emailError) {
                showNotification(`⚠️ Factura generada pero el email no se envió: ${result.data.emailError}`, 'warning');
              } else {
                showNotification('⚠️ Factura generada. No se envió el email. Verificá el correo o contactá al comprador.', 'warning');
              }
            }
          } else {
            throw new Error(result.message || result.error || 'Error desconocido en AFIP');
          }
        } catch (error) {
          console.error('💥 ERROR EN FACTURACIÓN AFIP:', error);

          if (error.message?.includes('Error de validación en AFIP')) {
            showNotification('❌ Error de validación AFIP. Revise los datos del cliente.', 'error');
          } else if (error.message?.includes('Timeout')) {
            showNotification('⏱️ Timeout en AFIP. La venta se registrará sin comprobante.', 'warning');
          } else if (error.message?.includes('AFIP no disponible')) {
            showNotification('🚫 AFIP temporalmente no disponible. Reintente más tarde.', 'warning');
          } else if (error.message?.includes('Error interno del servidor')) {
            showNotification('🔧 Error de configuración AFIP. Contacte al administrador.', 'error');
          } else {
            showNotification('❌ Error al generar comprobante fiscal. La venta se registrará sin factura.', 'warning');
          }

          saleData.invoiceError = {
            error: error.message || 'Error desconocido',
            errorCode: error.code || 'UNKNOWN_ERROR',
            timestamp: new Date().toISOString(),
            isAfipError: true
          };
        }
      }

      const saleRef = doc(collection(db, 'ventas_fisicas'));
      batch.set(saleRef, saleData);

      selectedProducts.forEach((product) => {
        if (product.excludeFromStock === true) return;
        const productRef = doc(db, 'products', product.id);
        batch.update(productRef, { stock: increment(-Number(product.quantity) || 0) });
      });

      await batch.commit();

      registerCustomerNewsletterEmails(saleData, 'ventas_fisicas').catch((err) => {
        console.error('[newsletter] registrar email cliente:', err);
      });

      // Id del documento en ventas_fisicas
      const saleId = saleRef.id;
      saleData.saleId = saleId;
      if (saleData.invoiceData) saleData.invoiceData.id = saleId;
      setSaleInfo(saleData);
      setShowModal(true);
      showNotification(
        isConsignment
          ? `Consignación #${orderNumber} registrada (no cobrada)`
          : `Venta #${orderNumber} registrada exitosamente`,
        'success'
      );
      if (oversellLines.length > 0) {
        showNotification(
          `Stock en negativo: ${oversellLines.map((line) => `${line.model} (${line.resultingStock})`).join(', ')}`,
          'warning'
        );
      }
      loadRecentSales();
      // Preparar la interfaz para la próxima venta manteniendo visible el modal actual
      resetForNextSale();
    } catch (error) {
      console.error("Error registering sale:", error);
      showNotification('Error al registrar la venta', 'error');
    } finally {
      setIsSubmitting(false);
      fetchProducts();
    }
  };

  // ===========================
  // FUNCIONES AUXILIARES
  // ===========================

  const resetComponent = () => {
    setSelectedProducts([]);
    setCustomerData({
      name: 'Patricio Paolini', cuit: '20-37232710-4', documentType: '80', phone: '', email: '',
      address: '', city: '', postalCode: '', province: '', country: 'Argentina', additionalInfo: '',
      ivaCondition: '5'
    });
    setDiscount({ type: 'percentage', value: 0 });
    setPaymentMethods(emptyPaymentMethods());
    setInvoiceOptions({
      generateInvoice: true,
      invoiceType: 'facturaC',
      sendByEmail: false,
      emailAddress: '',
      puntoVenta: 8
    });
    setShowModal(false);
    setSaleInfo(null);
    setSearchTerm('');
    setQuantityInputs({});
    localStorage.removeItem('localsales_backup');

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Resetea el formulario para la próxima venta sin cerrar el modal de éxito ni borrar saleInfo
  const resetForNextSale = () => {
    setSelectedProducts([]);
    setCustomerData({
      name: '', cuit: '', documentType: '99', phone: '', email: '',
      address: '', city: '', postalCode: '', province: '', country: 'Argentina', additionalInfo: '',
      ivaCondition: '5'
    });
    setDiscount({ type: 'percentage', value: 0 });
    setPaymentMethods(emptyPaymentMethods());
    setInvoiceOptions({
      generateInvoice: true,
      invoiceType: 'facturaC',
      sendByEmail: false,
      emailAddress: '',
      puntoVenta: 8
    });
    setSearchTerm('');
    setQuantityInputs({});
    localStorage.removeItem('localsales_backup');

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const filteredProducts = searchTerm
    ? products.filter(product =>
      product.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.id.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 10)
    : [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerData(prevData => ({ ...prevData, [name]: value }));

    if (name === 'email') {
      setInvoiceOptions(prev => ({
        ...prev,
        emailAddress: value
      }));
    }
  };

  const lastAfipLookupCuitRef = useRef('');

  const triggerAfipLookupForCuit = useCallback(async (rawCuit, { silentIfShort = false } = {}) => {
    if (!rawCuit) return;

    const digits = String(rawCuit).replace(/\D/g, '');
    if (!digits || digits.length < 8) {
      if (!silentIfShort) {
        showNotification('CUIT/DNI demasiado corto para consultar AFIP', 'warning');
      }
      return;
    }

    if (lastAfipLookupCuitRef.current === digits) {
      return;
    }
    lastAfipLookupCuitRef.current = digits;

    setIsCuitLookupLoading(true);

    let url = '';
    try {
      url = getAfipFunctionUrl('validateCuitAfip');
    } catch {
      url = '';
    }

    if (!url) {
      showNotification('No hay URL configurada para consultar AFIP. Revise config.', 'error');
      setIsCuitLookupLoading(false);
      return;
    }

    console.log('🔍 [CUIT] Consultando AFIP para:', digits, 'URL:', url);

    try {
      const response = await fetchWithTimeout(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ cuit: digits })
        },
        60000
      );

      const rawText = await response.text();
      let result = null;
      try {
        result = rawText ? JSON.parse(rawText) : null;
      } catch {
        result = null;
      }

      // Si no llegó JSON (p. ej. 500 plain text del Cloud Run), fallar permitiendo retry
      if (!result) {
        console.warn('⚠️ [CUIT] Respuesta no-JSON de AFIP:', response.status, rawText?.slice(0, 200));
        lastAfipLookupCuitRef.current = '';
        showNotification(
          `Error del servidor AFIP (HTTP ${response.status}). Redeployá la Cloud Function "validateCuitAfip" o cargá los datos manualmente.`,
          'error'
        );
        return;
      }

      if (!response.ok || !result.success || !result.data) {
        // Permitir reintentar si fue un error transitorio
        if (!response.ok) lastAfipLookupCuitRef.current = '';
        const msg = result.message || result.error || `AFIP respondió HTTP ${response.status}`;
        console.warn('⚠️ [CUIT] AFIP no encontró datos:', msg, result);
        showNotification(msg, response.ok ? 'warning' : 'error');
        return;
      }

      const data = result.data;
      const nombreAfip = (data.razonSocial || '').toString().trim();
      setCustomerData((prev) => ({
        ...prev,
        // Si el usuario no escribió nombre (o escribió vacío), completarlo con AFIP
        name: (prev.name && prev.name.trim()) ? prev.name : nombreAfip,
        cuit: (prev.cuit && prev.cuit.trim()) ? prev.cuit : rawCuit,
        documentType: digits.length >= 11 ? '80' : (prev.documentType || '96'),
        address: prev.address || (data.domicilioFiscal && data.domicilioFiscal.direccion) || '',
        city: prev.city || (data.domicilioFiscal && data.domicilioFiscal.localidad) || '',
        province: prev.province || (data.domicilioFiscal && data.domicilioFiscal.provincia) || '',
        postalCode: prev.postalCode || (data.domicilioFiscal && data.domicilioFiscal.codigoPostal) || ''
      }));

      console.log('✅ [CUIT] Datos AFIP aplicados:', nombreAfip);
      showNotification(`Datos obtenidos desde AFIP: ${nombreAfip || 'sin nombre'}`, 'success');
    } catch (error) {
      console.error('❌ [CUIT] Error consultando AFIP:', error);
      lastAfipLookupCuitRef.current = '';
      const msg = error?.name === 'AbortError'
        ? 'Tiempo de espera agotado consultando AFIP. Intente nuevamente.'
        : `Error al consultar AFIP: ${error?.message || 'desconocido'}`;
      showNotification(msg, 'error');
    } finally {
      setIsCuitLookupLoading(false);
    }
  }, []);

  const handleCuitBlur = useCallback(() => {
    const rawCuit = (customerData.cuit || '').trim();
    if (!rawCuit) return;
    triggerAfipLookupForCuit(rawCuit, { silentIfShort: true });
  }, [customerData.cuit, triggerAfipLookupForCuit]);

  const handleCuitChange = useCallback((e) => {
    const value = e.target.value;
    setCustomerData(prev => ({ ...prev, cuit: value }));

    const digits = value.replace(/\D/g, '');
    // Disparar al alcanzar 11 dígitos (CUIT/CUIL completo)
    if (digits.length === 11) {
      triggerAfipLookupForCuit(value);
    }
  }, [triggerAfipLookupForCuit]);

  const handleCuitKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const rawCuit = (e.target.value || '').trim();
      if (rawCuit) {
        // Reset del ref para permitir consulta manual con Enter aunque sea el mismo valor
        lastAfipLookupCuitRef.current = '';
        triggerAfipLookupForCuit(rawCuit);
      }
    }
  }, [triggerAfipLookupForCuit]);

  const handleQuantityInputChange = (productId, value) => {
    setQuantityInputs(prev => ({ ...prev, [productId]: value }));
  };

  const getSelectedPaymentMethods = () => formatPaymentMethods(paymentMethods, '');

  const getPaymentMethodsFrom = (methodsObj) => formatPaymentMethods(methodsObj);

  // ===========================
  // FUNCIONES ADICIONALES PARA GESTIÓN DE FACTURAS
  // ===========================

  const getInvoiceHistory = async (puntoVenta = null, limit = 10) => {
    try {
      const baseUrl = getAfipFunctionUrl('getInvoiceHistory');
      if (!baseUrl) {
        showNotification('URL de Cloud Functions no configurada.', 'error');
        return [];
      }
      const params = new URLSearchParams();
      if (puntoVenta) params.append('puntoVenta', puntoVenta);
      params.append('limit', limit);

      const response = await fetchWithTimeout(
        `${baseUrl}?${params}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } },
        30000
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      return result.facturas;

    } catch (error) {
      console.error('Error obteniendo historial:', error);
      showNotification('Error al obtener historial de facturas', 'error');
      return [];
    }
  };


  if (loading) return <Spinner />;

  const quotePreviewSaleData = showQuoteConfirm
    ? { ...buildQuotePayload(0), isDraft: true }
    : null;
  const quotePreviewEmail = (
    (customerData.email || '').trim() ||
    (invoiceOptions.emailAddress || '').trim()
  );

  // ===========================
  // RENDER PRINCIPAL
  // ===========================

  return (
    <div className="local-sales">
      <Notification
        message={notification.message}
        show={notification.show}
        type={notification.type}
        onClose={hideNotification}
      />

      <AfipProgressModal
        isVisible={isGeneratingInvoice}
        progress={invoiceProgress}
        error={invoiceError}
      />

      <header className="local-sales-hero">
        <div className="local-sales-hero-copy">
          <span className="local-sales-eyebrow">Venta en local</span>
          <h1 className="local-sales-title">Punto de venta</h1>
      
        </div>
        <div className="local-sales-hero-stats" aria-label="Resumen actual de la venta">
          <div className="local-sales-stat-card">
            <span>Productos</span>
            <strong>{selectedProducts.length}</strong>
          </div>
          <div className="local-sales-stat-card">
            <span>Unidades</span>
            <strong>{selectedProducts.reduce((total, product) => total + product.quantity, 0)}</strong>
          </div>
          <div className="local-sales-stat-card local-sales-stat-card-total">
            <span>Total</span>
            <strong>${formatNumber(calculations.finalTotal)}</strong>
          </div>
        </div>
      </header>

      <div className="local-sales-workspace">
        <section className="local-sales-panel local-sales-catalog-panel">
          <div className="local-sales-panel-header">
            <div>
              <span className="local-sales-section-kicker">Catálogo</span>
              <h2>Agregar productos</h2>
            </div>
            <span className="local-sales-panel-badge">{products.length} activos</span>
          </div>

          <div className="search-section">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar producto por nombre o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="local-sales-search-input"
            />
          </div>

          {searchTerm ? (
            <div className="local-sales-products-list">
              {filteredProducts.map(product => (
                <div key={product.id} className={`local-sales-product-item ${!product.excludeFromStock && product.stock <= 0 ? 'out-of-stock' : ''}`}>
                  <img src={product.images ? product.images[0] : ''} alt={product.model} className="local-sales-product-image" />
                  <div className="local-sales-product-info">
                    <span className="product-name">{product.model} - ${formatNumber(product.price)}</span>
                    <span className={`local-sales-stock-info ${product.excludeFromStock ? '' : product.stock <= 5 ? 'low-stock' : ''}`}>
                      {product.excludeFromStock
                        ? 'No computa stock'
                        : `Stock: ${product.stock}${product.stock <= 0 ? ' · se puede vender en negativo' : product.stock <= 5 ? ' ⚠️' : ''}`}
                    </span>
                    <div className="quantity-input-container">
                      <input
                        type="number"
                        min="1"
                        max={9999}
                        value={quantityInputs[product.id] || ''}
                        onChange={(e) => handleQuantityInputChange(product.id, e.target.value)}
                        onKeyDown={(e) => handleKeyPress(e, product, quantityInputs[product.id] || e.target.value)}
                        placeholder="Cant."
                        className="local-sales-quantity-input"
                      />
                      <button
                        onClick={() => addProductToSale(product, quantityInputs[product.id] || 1)}
                        className="add-product-btn"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredProducts.length === 0 && (
                <div className="local-sales-empty-state">
                  <strong>Sin coincidencias</strong>
                  <span>Probá con otro nombre o código de producto.</span>
                </div>
              )}
            </div>
          ) : (
            <div className="local-sales-empty-state local-sales-search-empty">
              <strong>Empezá buscando un producto</strong>
              <span>El listado aparece al escribir, usa tab o enter para agregar.</span>
            </div>
          )}
        </section>

        <section className="local-sales-panel local-sales-checkout-panel">
          <div className="selected-products-section">
        <h3 className="local-sales-selected-title">
          Productos Seleccionados ({selectedProducts.length})
        </h3>
        {selectedProducts.length === 0 ? (
          <p className="no-products-message">No hay productos seleccionados</p>
        ) : (
          <ul className="local-sales-selected-list">
            {selectedProducts.map(product => (
              <li key={product.id} className="local-sales-selected-item">
                <div className="product-info">
                  <div className="quantity-controls">
                    <button
                      onClick={() => updateProductQuantity(product.id, product.quantity - 1)}
                      className="quantity-btn"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={9999}
                      value={product.quantity}
                      onChange={(e) => updateProductQuantity(product.id, e.target.value)}
                      className="quantity-display"
                    />
                    <button
                      onClick={() => updateProductQuantity(product.id, product.quantity + 1)}
                      className="quantity-btn"
                    >
                      +
                    </button>
                  </div>
                  <span className="product-name">{product.model}</span>
                  {oversellLines.some((line) => line.id === product.id) ? (
                    <span className="local-sales-oversell-badge">
                      Stock {catalogStockOf(product.id, product)} → {catalogStockOf(product.id, product) - product.quantity}
                    </span>
                  ) : null}
                </div>
                <div className="local-sales-selected-item-actions">
                  <div className="price-input-container">
                    <span>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={product.price}
                      onChange={(e) => handlePriceChange(product.id, e.target.value)}
                      className="local-sales-price-input"
                    />
                  </div>
                  <span className="local-sales-product-total">
                    ${formatNumber(product.price * product.quantity)}
                  </span>
                  <button
                    onClick={() => removeProductFromSale(product.id)}
                    className="local-sales-remove-btn"
                  >
                    🗑️
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
          </div>



      {selectedProducts.length > 0 && (
        <section className="local-sales-customer-section">
          <h3>Cliente</h3>
          <p className="quote-options-hint">
            Buscá un cliente de ventas o de presupuestos enviados. Los datos sirven para factura AFIP, consignación y presupuesto.
          </p>
          <div className="frequent-client-search-wrap" ref={clientSearchRef}>
            <input
              type="text"
              placeholder="Buscar por nombre, CUIT o email"
              value={clientSearchTerm}
              onChange={(e) => {
                setClientSearchTerm(e.target.value);
                setShowClientDropdown(true);
              }}
              onFocus={() => frequentCustomers.length > 0 && setShowClientDropdown(true)}
              onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
              className="local-sales-customer-input frequent-client-search-input"
              aria-autocomplete="list"
            />
            {showClientDropdown && filteredFrequentClients.length > 0 && (
              <ul className="frequent-client-dropdown" role="listbox">
                {filteredFrequentClients.map((client, idx) => (
                  <li
                    key={`${client.source}-${client.name}-${client.cuit}-${idx}`}
                    role="option"
                    aria-selected="false"
                    className="frequent-client-dropdown-item"
                    onMouseDown={(e) => { e.preventDefault(); selectFrequentClient(client); }}
                  >
                    <span className="frequent-client-name">{client.name}</span>
                    <span className="frequent-client-meta">
                      {client.source === 'presupuesto' ? (
                        <span className="frequent-client-source">Presupuesto</span>
                      ) : (
                        <span className="frequent-client-source is-sale">Venta</span>
                      )}
                      {client.cuit ? <span className="frequent-client-cuit">CUIT: {client.cuit}</span> : null}
                      {client.email ? <span className="frequent-client-email">{client.email}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="customer-basic-info invoice-details-customer">
            <CuitInput
              value={customerData.cuit}
              onChange={handleCuitChange}
              onBlur={handleCuitBlur}
              onKeyDown={handleCuitKeyDown}
              isLoading={isCuitLookupLoading}
              highlighted
              required={invoiceOptions.generateInvoice && (invoiceOptions.invoiceType === 'facturaA' || invoiceOptions.invoiceType === 'facturaB')}
              placeholder={`CUIT/DNI ${(invoiceOptions.generateInvoice && (invoiceOptions.invoiceType === 'facturaA' || invoiceOptions.invoiceType === 'facturaB')) ? '*' : '(opcional)'}`}
            />

            <div className="basic-info-field">
              <label htmlFor="ls-ivaCondition" className="basic-info-field-label">
                Condición IVA
              </label>
              <select
                id="ls-ivaCondition"
                name="ivaCondition"
                value={customerData.ivaCondition || '5'}
                onChange={handleInputChange}
                className="local-sales-customer-input customer-basic-info-select-iva"
              >
                <option value="1">Responsable Inscripto</option>
                <option value="3">Exento</option>
                <option value="4">No Responsable</option>
                <option value="5">Consumidor Final</option>
                <option value="6">Responsable Monotributo</option>
                <option value="8">No Categorizado</option>
                <option value="9">Resp. Inscripto - Agente Percepción</option>
                <option value="10">Resp. Inscripto - Agente Retención</option>
              </select>
            </div>

            <div className="basic-info-field">
              <label htmlFor="ls-documentType" className="basic-info-field-label">
                Tipo de documento
              </label>
              <select
                id="ls-documentType"
                name="documentType"
                value={customerData.documentType}
                onChange={handleInputChange}
                className="local-sales-customer-input"
              >
                <option value="80">CUIT</option>
                <option value="86">CUIL</option>
                <option value="96">DNI</option>
                <option value="99">Sin identificar</option>
              </select>
            </div>

            <div className="basic-info-field">
              <label htmlFor="ls-customerName" className="basic-info-field-label">
                Nombre del cliente{paymentMethods.consignacion ? ' *' : ''}
              </label>
              <input
                id="ls-customerName"
                type="text"
                name="name"
                placeholder={paymentMethods.consignacion ? 'Nombre *' : 'Nombre (opcional)'}
                value={customerData.name}
                onChange={handleInputChange}
                className="local-sales-customer-input"
              />
            </div>

            <div className="basic-info-field">
              <label htmlFor="ls-customerPhone" className="basic-info-field-label">
                Teléfono
              </label>
              <input
                id="ls-customerPhone"
                type="text"
                name="phone"
                placeholder="Teléfono (opcional)"
                value={customerData.phone || ''}
                onChange={handleInputChange}
                className="local-sales-customer-input local-sales-customer-input-phone"
              />
            </div>
          </div>
          <EmailInput
            value={invoiceOptions.emailAddress || customerData.email || ''}
            onChange={(e) => {
              const value = e.target.value;
              setCustomerData((prev) => ({ ...prev, email: value }));
              handleInvoiceOptionChange('emailAddress', value);
            }}
            highlighted={shouldHighlightEmail}
          />
          <div className="optional-extra-address-section">
            <button
              type="button"
              className="optional-extra-address-trigger"
              onClick={() => setShowExtraAddressFields(s => !s)}
              aria-expanded={showExtraAddressFields}
            >
              {showExtraAddressFields ? '▼' : '+'} Agregar más datos (dirección, ciudad, provincia, país, código postal)
            </button>
            {showExtraAddressFields && (
              <ExtraAddressFields
                customerData={customerData}
                onChange={handleInputChange}
                idPrefix="ls-customer"
              />
            )}
          </div>
        </section>
      )}

      {/* SECCIÓN MODIFICADA: Medios de Pago con radio buttons */}
      {selectedProducts.length > 0 && (
        <div className="payment-methods-section">
          <h3>Medio de Pago (selecciona uno)</h3>
          <div className="payment-methods-grid">
            {Object.entries(paymentMethods).map(([method, isSelected]) => (
              <label key={method} className="payment-method-checkbox">
                <input
                  type="radio"
                  name="paymentMethod"
                  checked={isSelected}
                  onChange={() => handlePaymentMethodChange(method)}
                />
                <span className="payment-method-label">
                  {formatPaymentMethodLabel(method)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}



      {selectedProducts.length > 0 && (
        <div className="totals-section">
          <div className="local-sales-discount-container">
            <div className="discount-type-selector">
              <label>Tipo de descuento:</label>
              <div className="discount-type-buttons">
                <button
                  type="button"
                  className={`discount-type-btn ${discount.type === 'percentage' ? 'active' : ''}`}
                  onClick={() => setDiscount({ type: 'percentage', value: 0 })}
                >
                  % Porcentaje
                </button>
                <button
                  type="button"
                  className={`discount-type-btn ${discount.type === 'fixed' ? 'active' : ''}`}
                  onClick={() => setDiscount({ type: 'fixed', value: 0 })}
                >
                  $ Monto fijo
                </button>
              </div>
            </div>

            <div className="discount-input-container">
              <label>
                {discount.type === 'percentage' ? 'Descuento (%)' : 'Descuento ($)'}:
              </label>
              <div className="discount-input-wrapper">
                {discount.type === 'fixed' && <span className="currency-symbol">$</span>}
                <input
                  type="number"
                  min="0"
                  max={discount.type === 'percentage' ? "100" : calculations.subtotal.toString()}
                  step={discount.type === 'percentage' ? "0.1" : "0.01"}
                  value={discount.value}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    if (discount.type === 'percentage' && value <= 100) {
                      setDiscount({ ...discount, value });
                    } else if (discount.type === 'fixed' && value <= calculations.subtotal) {
                      setDiscount({ ...discount, value });
                    }
                  }}
                  className="local-sales-discount-input"
                  placeholder="0"
                />
                {discount.type === 'percentage' && <span className="percentage-symbol">%</span>}
              </div>
              {discount.type === 'fixed' && discount.value > calculations.subtotal && (
                <span className="discount-warning">
                  El descuento no puede ser mayor al subtotal
                </span>
              )}
            </div>
          </div>



          <div className="totals-breakdown">
            <div className="total-line">
              <span>Tipos de Productos:</span>
              <span>{selectedProducts.length}</span>
            </div>
            <div className="total-line">
              <span>Total Unidades:</span>
              <span>{selectedProducts.reduce((total, product) => total + product.quantity, 0)}</span>
            </div>
            <div className="total-line">
              <span>Subtotal:</span>
              <span>${formatNumber(calculations.subtotal)}</span>
            </div>
            {calculations.discountAmount > 0 && (
              <div className="total-line discount">
                <span>
                  Descuento ({discount.type === 'percentage' ? `${discount.value}%` : `${formatNumber(discount.value)}`}):
                </span>
                <span>-${formatNumber(calculations.discountAmount)}</span>
              </div>
            )}
            <div className="total-line final-total">
              <span>Total:</span>
              <span>${formatNumber(calculations.finalTotal)}</span>
            </div>

            {paymentMethods.consignacion && (
              <div className="consignment-notice" role="status">
                <strong>Consignación:</strong> se registra como venta no cobrada. El stock se descuenta
                y los datos de envío indican consignación. No se emite factura AFIP hasta que se cobre.
                Si el cliente tiene email, se le envía un aviso automático.
              </div>
            )}

            {Object.values(paymentMethods).some(method => method) && (
              <div className="total-line payment-method">
                <span>💳 Medio de Pago:</span>
                <span>{formatPaymentMethods(paymentMethods, '')}</span>
              </div>
            )}

            {invoiceOptions.generateInvoice && !paymentMethods.consignacion && (
              <div className="total-line invoice-info">
                <span>{getInvoiceTypeName(invoiceOptions.invoiceType)} - PV {String(invoiceOptions.puntoVenta).padStart(4, '0')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedProducts.length > 0 && (
        <div className="local-sales-tools">
          {!paymentMethods.consignacion && (
            <div className="invoice-options-section invoice-options-inline">
              <div className="local-sales-tool-head">
                <h3 className="invoice-inline-title">
                  Facturación AFIP
                  <span className="invoice-inline-pv-badge">
                    PV {String(invoiceOptions.puntoVenta).padStart(4, '0')}
                  </span>
                </h3>
                <p className="quote-options-hint">
                  Solo tipo de comprobante y punto de venta. El cliente ya está arriba.
                </p>
              </div>
              <label className="invoice-checkbox invoice-checkbox-no-margin">
                <input
                  type="checkbox"
                  checked={invoiceOptions.generateInvoice}
                  onChange={(e) => handleInvoiceOptionChange('generateInvoice', e.target.checked)}
                />
                <span>Generar comprobante fiscal con AFIP</span>
              </label>
              {invoiceOptions.generateInvoice && (
                <div className="invoice-details invoice-details-tight">
                  <div className="invoice-compact-controls">
                    <div className="invoice-type-selector">
                      <label>Tipo:</label>
                      <select
                        value={invoiceOptions.invoiceType}
                        onChange={(e) => handleInvoiceOptionChange('invoiceType', e.target.value)}
                        className="invoice-type-select invoice-type-select-compact"
                      >
                        <option value="facturaC">Factura C (Consumidor Final)</option>
                        <option value="facturaB">Factura B (Responsable Inscripto)</option>
                        <option value="facturaA">Factura A (Responsable Inscripto)</option>
                        <option value="remito">Remito</option>
                      </select>
                    </div>
                    <PuntoVentaSelector
                      value={invoiceOptions.puntoVenta}
                      onChange={(newPV) => handleInvoiceOptionChange('puntoVenta', newPV)}
                      disabled={isSubmitting || isGeneratingInvoice}
                      showNotification={showNotification}
                      testAfipConnection={testAfipConnection}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="quote-options-section">
            <div className="local-sales-tool-head">
              <h3 className="invoice-inline-title">Presupuesto</h3>
              <p className="quote-options-hint">
                No descuenta stock ni factura AFIP. Se guarda en Clientes.
              </p>
            </div>
            <div className="quote-options-grid">
              <label htmlFor="ls-quote-currency">
                Moneda
                <input
                  id="ls-quote-currency"
                  type="text"
                  value={quoteCurrency}
                  onChange={(e) => setQuoteCurrency(e.target.value)}
                  className="local-sales-customer-input"
                />
              </label>
              <label htmlFor="ls-quote-destination">
                Prov. Destino
                <input
                  id="ls-quote-destination"
                  type="text"
                  value={quoteDestination}
                  onChange={(e) => setQuoteDestination(e.target.value)}
                  className="local-sales-customer-input"
                />
              </label>
              <label htmlFor="ls-quote-valid-until">
                Válido hasta
                <input
                  id="ls-quote-valid-until"
                  type="date"
                  value={quoteValidUntil}
                  onChange={(e) => setQuoteValidUntil(e.target.value)}
                  className="local-sales-customer-input"
                />
              </label>
              <label htmlFor="ls-quote-payment" className="quote-field-wide">
                Forma de pago
                <input
                  id="ls-quote-payment"
                  type="text"
                  value={quotePaymentTerms}
                  onChange={(e) => setQuotePaymentTerms(e.target.value)}
                  className="local-sales-customer-input"
                  placeholder="A convenir, transferencia, 30 días…"
                />
              </label>
            </div>
            <div className="quote-options-checks">
              <label htmlFor="ls-quote-include-images" className="quote-check-label">
                <input
                  id="ls-quote-include-images"
                  type="checkbox"
                  checked={quoteIncludeImages}
                  onChange={(e) => setQuoteIncludeImages(e.target.checked)}
                />
                Incluir imágenes
              </label>
              <label htmlFor="ls-quote-include-description" className="quote-check-label">
                <input
                  id="ls-quote-include-description"
                  type="checkbox"
                  checked={quoteIncludeDescription}
                  onChange={(e) => setQuoteIncludeDescription(e.target.checked)}
                />
                Incluir descripción
              </label>
            </div>
            <label htmlFor="ls-quote-notes" className="quote-notes-label quote-field-wide">
              Notas (opcionales)
              <textarea
                id="ls-quote-notes"
                rows={2}
                value={quoteNotes}
                onChange={(e) => setQuoteNotes(e.target.value)}
                className="local-sales-customer-input"
                placeholder="Condiciones, plazos, observaciones para el cliente"
              />
            </label>
          </div>
        </div>
      )}

          <div className="local-sales-submit-container">
            <button
              onClick={() => setShowConfirmDialog(true)}
              className={`local-sales-submit-btn ${selectedProducts.length === 0 ||
                  !getSelectedPaymentMethods() ||
                  (paymentMethods.consignacion && !(customerData.name || '').trim()) ||
                  (invoiceOptions.generateInvoice && !paymentMethods.consignacion && !validatePuntoVenta(invoiceOptions.puntoVenta))
                  ? 'local-sales-submit-btn-disabled'
                  : ''
                }`}
              disabled={
                selectedProducts.length === 0 ||
                isSubmitting ||
                isGeneratingInvoice ||
                !getSelectedPaymentMethods() ||
                (paymentMethods.consignacion && !(customerData.name || '').trim()) ||
                (invoiceOptions.generateInvoice && !paymentMethods.consignacion && !validatePuntoVenta(invoiceOptions.puntoVenta))
              }
            >
              {paymentMethods.consignacion
                ? `Registrar consignación (no cobrada) - $${formatNumber(calculations.finalTotal)}`
                : `${invoiceOptions.generateInvoice ? `🏛️ PV${String(invoiceOptions.puntoVenta).padStart(4, '0')} ` : ''}Registrar Venta - $${formatNumber(calculations.finalTotal)}${invoiceOptions.generateInvoice ? ' + Factura AFIP' : ''}`}
            </button>
            {!quoteReadOnly && (
              <button
                type="button"
                onClick={openQuoteConfirm}
                className={`local-sales-quote-btn ${selectedProducts.length === 0 ? 'local-sales-submit-btn-disabled' : ''}`}
                disabled={selectedProducts.length === 0 || isSubmitting || isSavingQuote || isGeneratingInvoice}
              >
                {isSavingQuote
                  ? 'Guardando presupuesto…'
                  : `Vista previa del presupuesto - $${formatNumber(calculations.finalTotal)}`}
              </button>
            )}
            {(isSubmitting || isGeneratingInvoice || isSavingQuote) && (
              <div className="submit-loading-state">
                <Spinner />
                {isGeneratingInvoice && (
                  <p className="submit-loading-message">
                    {invoiceProgress || 'Procesando con AFIP...'}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <section className={`recent-local-sales-panel ${showDailySales ? 'is-open' : 'is-collapsed'}`} aria-label="Ventas del día">
        <div className="recent-local-sales-header">
          <div>
            <span className="local-sales-section-kicker">Operaciones del día</span>
            <h2>Ventas del día</h2>
          </div>
          <div className="recent-local-sales-header-actions">
            <button
              type="button"
              className="recent-local-sales-toggle"
              onClick={() => setShowDailySales(prev => !prev)}
              aria-expanded={showDailySales}
            >
              {showDailySales
                ? 'Ocultar ventas del día'
                : recentSales.length > 0
                  ? `Ver ventas del día (${recentSales.length})`
                  : 'Ver ventas del día'}
            </button>
            {showDailySales && (
              <button
                type="button"
                className="recent-local-sales-refresh"
                onClick={loadRecentSales}
                disabled={recentSalesLoading}
              >
                {recentSalesLoading ? 'Actualizando...' : 'Actualizar'}
              </button>
            )}
          </div>
        </div>

        {showDailySales && (
          recentSales.length === 0 ? (
            <div className="recent-local-sales-empty">
              {recentSalesLoading ? 'Cargando ventas del día...' : 'Todavía no hay ventas registradas hoy.'}
            </div>
          ) : (
            <div className="recent-local-sales-table-wrap">
              <table className="recent-local-sales-table">
                <thead>
                  <tr>
                    <th>Venta</th>
                    <th>Hora</th>
                    <th>Cliente</th>
                    <th>Pago</th>
                    <th>Total</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSales.map((sale) => (
                    <tr key={sale.id}>
                      <td>#{sale.orderNumber}</td>
                      <td>{formatSaleDate(sale.date)}</td>
                      <td>{getCustomerNameFromSale(sale)}</td>
                      <td>
                        {isConsignmentSale(sale)
                          ? 'Consignación (no cobrada)'
                          : formatPaymentMethods(sale.paymentMethods, '—')}
                      </td>
                      <td>${formatNumber(Number(sale.totalPrice) || 0)}</td>
                      <td>
                        <div
                          className={`recent-sale-actions-wrapper${recentMenuFor === sale.id ? ' is-open' : ''}`}
                          ref={recentMenuFor === sale.id ? recentMenuRef : null}
                        >
                          <button
                            type="button"
                            className="recent-sale-menu-trigger"
                            onClick={() => setRecentMenuFor(prev => prev === sale.id ? null : sale.id)}
                            aria-label={`Opciones para venta ${sale.orderNumber}`}
                            aria-expanded={recentMenuFor === sale.id}
                            aria-haspopup="true"
                          >
                            ⋮
                          </button>
                          {recentMenuFor === sale.id && (
                            <div className="recent-sale-menu-dropdown" role="menu">
                              <button type="button" role="menuitem" onClick={() => openRecentSaleTicket(sale)}>
                                Imprimir ticket
                              </button>
                              <button type="button" role="menuitem" onClick={() => printRecentShippingLabel(sale)}>
                                Crear rótulo
                              </button>
                              <button type="button" role="menuitem" onClick={() => openRecentRemito(sale)}>
                                Imprimir remito
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>

      {showConfirmDialog && (
        <div className="local-sales-modal" onClick={() => setShowConfirmDialog(false)}>
          <div className="local-sales-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{paymentMethods.consignacion ? 'Confirmar consignación' : 'Confirmar Venta'}</h3>
            <p>
              {paymentMethods.consignacion
                ? 'Se registrará como mercadería en consignación (venta no cobrada). El stock se descuenta y el envío indica consignación.'
                : '¿Está seguro que desea registrar esta venta?'}
            </p>
            {oversellLines.length > 0 && (
              <p className="confirm-consignment-warning" role="status">
                Esta venta deja stock en negativo:{' '}
                {oversellLines.map((line) => `${line.model} (${line.stock} → ${line.resultingStock})`).join(', ')}.
              </p>
            )}

            <div className="confirm-products-list">
              <h4>Productos a vender:</h4>
              <ul className="sale-summary">
                {selectedProducts.map((product, index) => (
                  <li key={index} className="sale-summary-item">
                    <img
                      src={product.images ? product.images[0] : ''}
                      alt={product.model}
                      className="local-sales-product-image"
                    />
                    <div>
                      <div>{product.model}</div>
                      <div>{product.quantity} × ${formatNumber(product.price)} = ${formatNumber(product.price * product.quantity)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="confirm-details">
              <p><strong>Productos:</strong> {selectedProducts.length}</p>
              <p><strong>Total:</strong> ${formatNumber(calculations.finalTotal)}</p>
              {calculations.discountAmount > 0 && (
                <p>
                  <strong>Descuento:</strong>
                  {discount.type === 'percentage' ? ` ${discount.value}%` : ` ${formatNumber(discount.value)}`}
                  {` (-${formatNumber(calculations.discountAmount)})`}
                </p>
              )}
              <p><strong>Medio de pago:</strong> {getSelectedPaymentMethods()}</p>
              {paymentMethods.consignacion && (
                <p><strong>Cliente:</strong> {customerData.name}</p>
              )}
              {paymentMethods.consignacion && (
                <p className="confirm-consignment-warning">Esta operación queda pendiente de cobro.</p>
              )}
              {paymentMethods.consignacion && (
                <p>
                  <strong>Email:</strong>{' '}
                  {((customerData.email || invoiceOptions.emailAddress || '').includes('@')
                    ? `se enviará aviso a ${(customerData.email || invoiceOptions.emailAddress || '').trim()}`
                    : 'sin email — no se envía aviso')}
                </p>
              )}
              {invoiceOptions.generateInvoice && !paymentMethods.consignacion && (
                <div>
                  <p><strong>Comprobante:</strong> {getInvoiceTypeName(invoiceOptions.invoiceType)}</p>
                  <p><strong>Punto de Venta:</strong> {String(invoiceOptions.puntoVenta).padStart(4, '0')}</p>
                  <p><strong>Cliente:</strong> {customerData.name || 'Consumidor Final'}</p>
                  {invoiceOptions.emailAddress && invoiceOptions.emailAddress.includes('@') && (
                    <p><strong>Envío email:</strong> {invoiceOptions.emailAddress}</p>
                  )}
                  {!validatePuntoVenta(invoiceOptions.puntoVenta) && (
                    <p className="confirm-invalid-pv-warning">
                      ⚠️ Advertencia: Punto de venta inválido
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="local-sales-modal-actions">
              <button onClick={() => setShowConfirmDialog(false)} className="cancel-btn">
                Cancelar
              </button>
              <button
                onClick={handleSaleSubmit}
                className="confirm-btn"
                disabled={
                  invoiceOptions.generateInvoice &&
                  !validatePuntoVenta(invoiceOptions.puntoVenta)
                }
              >
                {paymentMethods.consignacion
                  ? 'Confirmar consignación (no cobrada)'
                  : invoiceOptions.generateInvoice
                    ? `🏛️ Confirmar Venta + AFIP PV${String(invoiceOptions.puntoVenta).padStart(4, '0')}`
                    : 'Confirmar Venta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuoteConfirm && quotePreviewSaleData && (
        <div
          className="local-sales-modal is-quote-preview"
          onClick={() => {
            if (!isSavingQuote && !isSendingQuoteEmail) setShowQuoteConfirm(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="local-sales-modal-content local-sales-quote-modal" onClick={(e) => e.stopPropagation()}>
            <div className="local-sales-quote-modal-head">
              <div className="local-sales-quote-modal-copy">
                <h2>Vista previa del presupuesto</h2>
                <p>Revisá el PDF antes de guardar o enviar. No descuenta stock ni factura AFIP.</p>
                <p><strong>Cliente:</strong> {quotePreviewSaleData.customerData?.name}</p>
                <p><strong>Total:</strong> ${formatNumber(quotePreviewSaleData.totalPrice)}</p>
                <p>
                  <strong>Email:</strong>{' '}
                  {quotePreviewEmail.includes('@')
                    ? quotePreviewEmail
                    : 'sin email — podés guardar e imprimir, no enviar'}
                </p>
              </div>
              <div className="local-sales-quote-modal-actions">
                <button
                  type="button"
                  className="local-sales-quote-modal-btn is-back"
                  onClick={() => setShowQuoteConfirm(false)}
                  disabled={isSavingQuote || isSendingQuoteEmail}
                >
                  Volver
                </button>
                <button
                  type="button"
                  className="local-sales-quote-modal-btn is-save"
                  onClick={() => handleQuoteSubmit({ sendEmail: false })}
                  disabled={isSavingQuote || isSendingQuoteEmail}
                >
                  {isSavingQuote && !isSendingQuoteEmail ? 'Guardando…' : 'Guardar'}
                </button>
                <button
                  type="button"
                  className="local-sales-quote-modal-btn is-send"
                  onClick={() => handleQuoteSubmit({ sendEmail: true })}
                  disabled={
                    isSavingQuote ||
                    isSendingQuoteEmail ||
                    !quotePreviewEmail.includes('@')
                  }
                >
                  {isSendingQuoteEmail ? 'Enviando…' : 'Guardar y enviar'}
                </button>
              </div>
            </div>
            {(isSavingQuote || isSendingQuoteEmail) && (
              <div className="submit-loading-state">
                <Spinner />
                <p className="submit-loading-message">
                  {isSendingQuoteEmail ? 'Guardando y enviando presupuesto…' : 'Guardando presupuesto…'}
                </p>
              </div>
            )}
            <InvoicePrinter
              saleData={quotePreviewSaleData}
              invoiceData={buildQuoteInvoiceData(quotePreviewSaleData)}
              title="Presupuesto"
              showPrintButton
              fullPage
            />
          </div>
        </div>
      )}

      {showModal && saleInfo && (
        <div className="local-sales-modal" onClick={() => setShowModal(false)}>
          <div className="local-sales-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{isConsignmentSale(saleInfo) ? '📦 Consignación registrada' : '✅ Venta Registrada'}</h2>
            <p><strong>Número de Orden:</strong> {saleInfo.orderNumber}</p>
            <p><strong>Total:</strong> ${formatNumber(saleInfo.totalPrice)}</p>
            <p><strong>Medio de pago:</strong> {getPaymentMethodsFrom(saleInfo.paymentMethods)}</p>
            {isConsignmentSale(saleInfo) && (
              <p className="sale-consignment-note">
                Venta no cobrada. El rótulo y el remito indican consignación.
              </p>
            )}
            {isConsignmentSale(saleInfo) && (saleInfo.customerData?.email || saleInfo.invoiceOptions?.emailAddress)?.includes('@') && (
              <p className="sale-success-email-note">
                📧 Aviso de consignación en envío a {saleInfo.customerData?.email || saleInfo.invoiceOptions?.emailAddress}
              </p>
            )}

            {saleInfo.invoiceData && saleInfo.invoiceOptions?.emailAddress && (
              <p className="sale-success-email-note">
                📧 Comprobante enviado por email a {saleInfo.invoiceOptions.emailAddress}
              </p>
            )}

            {saleInfo.invoiceError && (
              <div className="sale-invoice-error-box">
                <h4 className="sale-invoice-error-title">
                  ❌ Error en Facturación AFIP
                </h4>
                <p className="sale-invoice-error-message">
                  La venta se registró correctamente pero no se pudo generar el comprobante fiscal.
                </p>
                <p className="sale-invoice-error-detail">
                  Error: {saleInfo.invoiceError.error}
                </p>
              </div>
            )}

            <h3>Productos:</h3>
            <ul className="sale-summary">
              {saleInfo.products.map((product, index) => (
                <li key={index} className="sale-summary-item">
                  <img
                    src={product.images ? product.images[0] : ''}
                    alt={product.model}
                    className="local-sales-product-image"
                  />
                  <div>
                    <div>{product.model}</div>
                    <div>{product.quantity} × ${formatNumber(product.price)} = ${formatNumber(product.price * product.quantity)}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="local-sales-modal-actions">
              <ImprimirTicket saleInfo={saleInfo} onPrintClick={printTicketInNewWindow} />
            </div>
            <div className="sale-modal-history-action">
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setShowModal(false);
                  navigate('/historial-ventas');
                }}
              >
                📊 Ver Historial de Ventas
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemitoModal && remitoSaleInfo && (
        <div
          className="local-sales-modal remito-modal"
          onClick={() => {
            setShowRemitoModal(false);
            setRemitoSaleInfo(null);
          }}
        >
          <div className="local-sales-modal-content remito-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-modal-header">
              <h3>Remito de Transporte</h3>
              <button
                type="button"
                onClick={() => {
                  setShowRemitoModal(false);
                  setRemitoSaleInfo(null);
                }}
                className="close-modal-btn"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <RemitoPrintInline saleInfo={remitoSaleInfo} />
          </div>
        </div>
      )}

      {hasUnsavedChanges && <div className="auto-save-indicator">💾</div>}
    </div>
  );
};

export default LocalSales;